import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ToggleButtonGroup,
  ToggleButton,
  Tabs,
  Tab,
  Switch,
  Avatar,
  Checkbox,
  Select,
  MenuItem,
  FormControlLabel,
  Popover,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Folder as FolderIcon,
  Description as FileIcon,
  ArrowUpward as UpIcon,
  CalendarMonth as SessionIcon,
  Edit as EditIcon,
  DragIndicator as DragIcon,
  PictureAsPdf as PdfIcon,
  Close as CloseIcon,
  Autorenew as RefondreIcon,
  Settings as SettingsIcon,
  Casino as CasinoIcon,
  MenuBook as SolutionsIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import sessionService from '../../services/session.service';
import api from '../../services/api';
import sessionContainerService from '../../services/sessionContainerService';
import courseService from '../../services/course.service';
import patternService from '../../services/pattern.service';
import exerciseService from '../../services/exercise.service';
import courseContainerService from '../../services/courseContainerService';
import patternContainerService from '../../services/patternContainerService';
import exerciseContainerService from '../../services/exerciseContainerService';
import videoService from '../../services/video.service';
import videoContainerService from '../../services/videoContainer.service';
import userService from '../../services/user.service';
import { User, CompositePattern } from '../../types';
import ThumbnailPicker from '../../components/ThumbnailPicker';
import compositePatternService from '../../services/compositePattern.service';
import PatternInstanceModal from '../../components/PatternInstanceModal';
import CompositePatternInstanceModal from '../../components/CompositePatternInstanceModal';

interface SessionsProps {
  urlContainerId?: number | null;
  onNavChange?: (containerId: number | null, itemId: number | null, label?: string) => void;
}

const Sessions: React.FC<SessionsProps> = ({ urlContainerId, onNavChange }) => {
  const [sessions, setSessions] = useState<any[]>([]);
  const [containers, setContainers] = useState<any[]>([]);
  const [selectedSession, setSelectedSession] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentContainer, setCurrentContainer] = useState<number | null>(null);
  const [parentContainer, setParentContainer] = useState<number | null>(null);
  const [parentContainerTitle, setParentContainerTitle] = useState<string | undefined>(undefined);

  // Sync from URL (back/forward navigation)
  useEffect(() => {
    if (urlContainerId !== undefined && urlContainerId !== currentContainer) {
      setCurrentContainer(urlContainerId);
    }
  }, [urlContainerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load session preferences
  useEffect(() => {
    sessionService.getPreferences().then((prefs) => {
      setSepAfterCourse(prefs.separator_after_course || 'none');
      setSepAfterExercise(prefs.separator_after_exercise || 'none');
      setExerciseNumbering(prefs.exercise_numbering || false);
    }).catch(() => {});
  }, []);

  // Form state
  const [, setName] = useState('');
  const [description, setDescription] = useState('');
  const [reasons, setReasons] = useState<string[]>([]);
  const [, setIsPublic] = useState(false);
  const [sessionItems, setSessionItems] = useState<any[]>([]);

  // PDF state
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Selected items for partial preview
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
  const [partialPreviewOpen, setPartialPreviewOpen] = useState(false);
  const [partialPdfUrl, setPartialPdfUrl] = useState<string | null>(null);
  const [generatingPartialPdf, setGeneratingPartialPdf] = useState(false);
  const [fullPreviewOpen, setFullPreviewOpen] = useState(false);
  const [fullPdfUrl, setFullPdfUrl] = useState<string | null>(null);
  const [generatingFullPdf, setGeneratingFullPdf] = useState(false);
  const [shuffleLoading, setShuffleLoading] = useState(false);
  const [refondreLoading, setRefondreLoading] = useState(false);
  const [solutionsLoading, setSolutionsLoading] = useState(false);
  const [solutionsPdfUrl, setSolutionsPdfUrl] = useState<string | null>(null);
  const [solutionsPreviewOpen, setSolutionsPreviewOpen] = useState(false);

  // Session formatting preferences
  const [sepAfterCourse, setSepAfterCourse] = useState('none');
  const [sepAfterExercise, setSepAfterExercise] = useState('none');
  const [exerciseNumbering, setExerciseNumbering] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);

  // Composite config editor
  const [editCompositeConfigOpen, setEditCompositeConfigOpen] = useState(false);
  const [editingCompositeGroupId, setEditingCompositeGroupId] = useState<string | null>(null);
  const [editCompositePool, setEditCompositePool] = useState<Set<number>>(new Set());
  const [editCompositeCount, setEditCompositeCount] = useState<number>(1);
  const [editCompositeAllPatterns, setEditCompositeAllPatterns] = useState<{id: number; title: string}[]>([]);

  // Click and drag selection state
  const [isDraggingSelection, setIsDraggingSelection] = useState(false);
  const [dragStartAction, setDragStartAction] = useState<'select' | 'deselect' | null>(null);


  // Dialogs
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [createType, setCreateType] = useState<'container' | 'session'>('session');
  const [newContainerTitle, setNewContainerTitle] = useState('');
  const [newSessionName, setNewSessionName] = useState('');

  // Edit container dialog
  const [editContainerDialogOpen, setEditContainerDialogOpen] = useState(false);
  const [editingContainer, setEditingContainer] = useState<any | null>(null);
  const [editContainerTitle, setEditContainerTitle] = useState('');
  const [newContainerDescription, setNewContainerDescription] = useState('');
  const [editContainerDescription, setEditContainerDescription] = useState('');
  const [editContainerThumbnail, setEditContainerThumbnail] = useState<number | null>(null);
  const [showDescriptions, setShowDescriptions] = useState(false);

  // Edit session name dialog
  const [editSessionNameDialogOpen, setEditSessionNameDialogOpen] = useState(false);
  const [editingSessionName, setEditingSessionName] = useState('');

  // Pattern instance dialog
  const [patternInstanceModalOpen, setPatternInstanceModalOpen] = useState(false);
  const [selectedPatternForInstances, setSelectedPatternForInstances] = useState<any | null>(null);
  const [pendingDropDestinationIndex, setPendingDropDestinationIndex] = useState<number | null>(null);

  // Available courses, patterns and exercises
  const [courses, setCourses] = useState<any[]>([]);
  const [courseContainers, setCourseContainers] = useState<any[]>([]);
  const [currentCourseContainer, setCurrentCourseContainer] = useState<number | null>(null);
  const [parentCourseContainer, setParentCourseContainer] = useState<number | null>(null);

  const [patterns, setPatterns] = useState<any[]>([]);
  const [compositePatterns, setCompositePatterns] = useState<CompositePattern[]>([]);
  const [patternContainers, setPatternContainers] = useState<any[]>([]);
  const [currentPatternContainer, setCurrentPatternContainer] = useState<number | null>(null);
  const [parentPatternContainer, setParentPatternContainer] = useState<number | null>(null);

  // Composite pattern modal
  const [compositeModalOpen, setCompositeModalOpen] = useState(false);
  const [selectedCompositePattern, setSelectedCompositePattern] = useState<CompositePattern | null>(null);

  const [exercises, setExercises] = useState<any[]>([]);
  const [exerciseContainers, setExerciseContainers] = useState<any[]>([]);
  const [currentExerciseContainer, setCurrentExerciseContainer] = useState<number | null>(null);
  const [parentExerciseContainer, setParentExerciseContainer] = useState<number | null>(null);

  const [videos, setVideos] = useState<any[]>([]);
  const [videoContainers, setVideoContainers] = useState<any[]>([]);
  const [currentVideoContainer, setCurrentVideoContainer] = useState<number | null>(null);
  const [parentVideoContainer, setParentVideoContainer] = useState<number | null>(null);

  // For sessions tab - use separate navigation state to avoid conflict with left panel
  const [sessionsForTab, setSessionsForTab] = useState<any[]>([]);
  const [sessionContainersForTab, setSessionContainersForTab] = useState<any[]>([]);
  const [currentSessionContainerForTab, setCurrentSessionContainerForTab] = useState<number | null>(null);
  const [parentSessionContainerForTab, setParentSessionContainerForTab] = useState<number | null>(null);
  const [selectedSessionForTab, setSelectedSessionForTab] = useState<any | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [currentTab, setCurrentTab] = useState(1); // 0: Cours, 1: Patterns, 2: Exercices, 3: Séances, 4: Cours vidéo, 5: Enseignants

  const [teachers, setTeachers] = useState<User[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<User | null>(null);
  const [teacherSubTab, setTeacherSubTab] = useState(0); // 0: Cours, 1: Patterns, 2: Exercices, 3: Séances, 4: Cours vidéo

  // Teacher's content
  const [teacherCourses, setTeacherCourses] = useState<any[]>([]);
  const [teacherPatterns, setTeacherPatterns] = useState<any[]>([]);
  const [teacherExercises, setTeacherExercises] = useState<any[]>([]);
  const [teacherSessions, setTeacherSessions] = useState<any[]>([]);
  const [teacherVideos, setTeacherVideos] = useState<any[]>([]);
  const [selectedTeacherSession, setSelectedTeacherSession] = useState<any | null>(null);

  // Thumbnail preview
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState<string | null>(null);

  // Define these functions early so they can be used in useEffect dependencies
  const initialLoadDone = useRef(false);

  const loadSessions = useCallback(async () => {
    try {
      if (!initialLoadDone.current) setLoading(true);
      const params: any = {};
      if (currentContainer === null) {
        params.container = 'null';
      } else if (currentContainer) {
        params.container = currentContainer;
      }

      const data = await sessionService.getAll(params);
      setSessions(data);
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast.error('Erreur lors du chargement des séances');
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  }, [currentContainer]);

  const loadContainers = useCallback(async () => {
    try {
      const data = await sessionContainerService.getContainers(currentContainer);
      setContainers(data);

      // Load parent info if we're inside a container
      if (currentContainer !== null) {
        const containerInfo = await sessionContainerService.getContainer(currentContainer);
        setParentContainer(containerInfo.parent);
        if (containerInfo.parent !== null) {
          const parentInfo = await sessionContainerService.getContainer(containerInfo.parent);
          setParentContainerTitle(parentInfo.title);
        } else {
          setParentContainerTitle(undefined);
        }
      } else {
        setParentContainer(null);
        setParentContainerTitle(undefined);
      }
    } catch (error) {
      console.error('Error loading containers:', error);
    }
  }, [currentContainer]);

  const loadCourses = useCallback(async () => {
    try {
      const allCourses = await courseService.getCourses();
      // Filter courses by current container client-side
      const filteredByContainer = allCourses.filter((course: any) => {
        if (currentCourseContainer === null) {
          // Show only courses without a container (root level)
          return !course.container || course.container === null;
        }
        // Show courses in the current container
        return course.container === currentCourseContainer;
      });
      setCourses(filteredByContainer);
    } catch (error) {
      console.error('Error loading courses:', error);
      toast.error('Erreur lors du chargement des cours');
    }
  }, [currentCourseContainer]);

  const loadCourseContainers = useCallback(async () => {
    try {
      const data = await courseContainerService.getContainers(currentCourseContainer);
      setCourseContainers(data);

      // Load parent info if we're inside a container
      if (currentCourseContainer !== null) {
        const containerInfo = await courseContainerService.getContainer(currentCourseContainer);
        setParentCourseContainer(containerInfo.parent ?? null);
      } else {
        setParentCourseContainer(null);
      }
    } catch (error) {
      console.error('Error loading course containers:', error);
    }
  }, [currentCourseContainer]);

  const loadPatterns = useCallback(async () => {
    try {
      const containerId = currentPatternContainer;
      const [data, composites] = await Promise.all([
        patternService.getPatterns(containerId),
        compositePatternService.getAll({ container: containerId }),
      ]);
      setPatterns(Array.isArray(data) ? data : []);
      setCompositePatterns(composites);
    } catch (error) {
      console.error('Error loading patterns:', error);
      toast.error('Erreur lors du chargement des patterns');
    }
  }, [currentPatternContainer]);

  const loadPatternContainers = useCallback(async () => {
    try {
      const data = await patternContainerService.getContainers(currentPatternContainer);
      setPatternContainers(data);

      // Load parent info if we're inside a container
      if (currentPatternContainer !== null) {
        const currentContainerData = await patternContainerService.getContainer(currentPatternContainer);
        setParentPatternContainer(currentContainerData?.parent ?? null);
      } else {
        setParentPatternContainer(null);
      }
    } catch (error) {
      console.error('Error loading pattern containers:', error);
    }
  }, [currentPatternContainer]);

  const loadExercises = useCallback(async () => {
    try {
      const allExercises = await exerciseService.getExercises();
      // Filter exercises by current container client-side
      const filteredByContainer = allExercises.filter((exercise: any) => {
        if (currentExerciseContainer === null) {
          // Show only exercises without a container (root level)
          return !exercise.container || exercise.container === null;
        }
        // Show exercises in the current container
        return exercise.container === currentExerciseContainer;
      });
      setExercises(filteredByContainer);
    } catch (error) {
      console.error('Error loading exercises:', error);
      toast.error('Erreur lors du chargement des exercices');
    }
  }, [currentExerciseContainer]);

  const loadExerciseContainers = useCallback(async () => {
    try {
      const data = await exerciseContainerService.getContainers(currentExerciseContainer);
      setExerciseContainers(data);

      // Load parent info if we're inside a container
      if (currentExerciseContainer !== null) {
        // Find the current container to get its parent
        const allContainers = await exerciseContainerService.getContainers(null);
        const currentContainerData = allContainers.find((c: any) => c.id === currentExerciseContainer);
        setParentExerciseContainer(currentContainerData?.parent ?? null);
      } else {
        setParentExerciseContainer(null);
      }
    } catch (error) {
      console.error('Error loading exercise containers:', error);
    }
  }, [currentExerciseContainer]);

  const loadVideos = useCallback(async () => {
    try {
      const allVideos = await videoService.getAll();
      // Filter videos by current container client-side
      const filteredByContainer = allVideos.filter((video: any) => {
        if (currentVideoContainer === null) {
          // Show only videos without a container (root level)
          return !video.container || video.container === null;
        }
        // Show videos in the current container
        return video.container === currentVideoContainer;
      });
      setVideos(filteredByContainer);
    } catch (error) {
      console.error('Error loading videos:', error);
      toast.error('Erreur lors du chargement des vidéos');
    }
  }, [currentVideoContainer]);

  const loadVideoContainers = useCallback(async () => {
    try {
      const data = await videoContainerService.getContainers(currentVideoContainer);
      setVideoContainers(data);

      // Load parent info if we're inside a container
      if (currentVideoContainer !== null) {
        const containerInfo = await videoContainerService.getContainer(currentVideoContainer);
        setParentVideoContainer(containerInfo.parent ?? null);
      } else {
        setParentVideoContainer(null);
      }
    } catch (error) {
      console.error('Error loading video containers:', error);
    }
  }, [currentVideoContainer]);

  const loadSessionsForTab = useCallback(async () => {
    try {
      const params: any = {};
      if (currentSessionContainerForTab === null) {
        params.container = 'null';
      } else if (currentSessionContainerForTab) {
        params.container = currentSessionContainerForTab;
      }

      const data = await sessionService.getAll(params);
      setSessionsForTab(data);
    } catch (error) {
      console.error('Error loading sessions for tab:', error);
      toast.error('Erreur lors du chargement des séances');
    }
  }, [currentSessionContainerForTab]);

  const loadSessionContainersForTab = useCallback(async () => {
    try {
      const data = await sessionContainerService.getContainers(currentSessionContainerForTab);
      setSessionContainersForTab(data);

      // Load parent info if we're inside a container
      if (currentSessionContainerForTab !== null) {
        const containerInfo = await sessionContainerService.getContainer(currentSessionContainerForTab);
        setParentSessionContainerForTab(containerInfo.parent ?? null);
      } else {
        setParentSessionContainerForTab(null);
      }
    } catch (error) {
      console.error('Error loading session containers for tab:', error);
    }
  }, [currentSessionContainerForTab]);

  const loadTeachers = useCallback(async () => {
    try {
      const data = await userService.getTeachers();
      setTeachers(data);
    } catch (error) {
      console.error('Error loading teachers:', error);
      toast.error('Erreur lors du chargement des enseignants');
    }
  }, []);

  const loadTeacherContent = useCallback(async (teacherId: number) => {
    try {
      // Load teacher's courses
      const coursesData = await courseService.getCourses();
      const teacherCoursesFiltered = coursesData.filter((course: any) => course.created_by === teacherId);
      setTeacherCourses(teacherCoursesFiltered);

      // Load teacher's patterns
      const patternsData = await patternService.getAllPatterns();
      const teacherPatternsFiltered = patternsData.filter((pattern: any) => pattern.created_by === teacherId);
      setTeacherPatterns(teacherPatternsFiltered);

      // Load teacher's exercises
      const exercisesData = await exerciseService.getExercises();
      const teacherExercisesFiltered = exercisesData.filter((exercise: any) => exercise.created_by === teacherId);
      setTeacherExercises(teacherExercisesFiltered);

      // Load teacher's sessions
      const sessionsData = await sessionService.getAll();
      const teacherSessionsFiltered = sessionsData.filter((session: any) => session.created_by === teacherId);
      setTeacherSessions(teacherSessionsFiltered);

      // Load teacher's videos
      const videosData = await videoService.getAll();
      const teacherVideosFiltered = videosData.filter((video: any) => video.created_by === teacherId);
      setTeacherVideos(teacherVideosFiltered);
    } catch (error) {
      console.error('Error loading teacher content:', error);
      toast.error('Erreur lors du chargement du contenu de l\'enseignant');
    }
  }, []);

  useEffect(() => {
    loadSessions();
    loadContainers();
  }, [currentContainer, loadSessions, loadContainers]);

  useEffect(() => {
    loadCourses();
    loadCourseContainers();
  }, [currentCourseContainer, loadCourses, loadCourseContainers]);

  useEffect(() => {
    loadPatterns();
    loadPatternContainers();
  }, [currentPatternContainer, loadPatterns, loadPatternContainers]);

  useEffect(() => {
    loadExercises();
    loadExerciseContainers();
  }, [currentExerciseContainer, loadExercises, loadExerciseContainers]);

  useEffect(() => {
    loadVideos();
    loadVideoContainers();
  }, [currentVideoContainer, loadVideos, loadVideoContainers]);

  useEffect(() => {
    loadSessionsForTab();
    loadSessionContainersForTab();
  }, [currentSessionContainerForTab, loadSessionsForTab, loadSessionContainersForTab]);

  useEffect(() => {
    loadTeachers();
  }, [loadTeachers]);

  const handleToggleSessionPublic = async (sessionId: number, newIsPublic: boolean) => {
    try {
      await sessionService.update(sessionId, { is_public: newIsPublic });

      toast.success(`Séance rendue ${newIsPublic ? 'publique' : 'privée'}`);

      // Update in the list
      setSessions(sessions.map(s => s.id === sessionId ? { ...s, is_public: newIsPublic } : s));

      // Update selected session if it's the same
      if (selectedSession?.id === sessionId) {
        setSelectedSession({ ...selectedSession, is_public: newIsPublic });
        setIsPublic(newIsPublic);
      }
    } catch (error: any) {
      console.error('Error toggling session public status:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || 'Erreur lors de la mise à jour du statut';
      toast.error(errorMsg);
    }
  };

  const handleUpdateSessionDescription = async (newDescription: string) => {
    if (!selectedSession) return;

    try {
      await sessionService.update(selectedSession.id, { description: newDescription });
      setSelectedSession({ ...selectedSession, description: newDescription });
      loadSessions(); // Refresh the list
    } catch (error) {
      console.error('Error updating session description:', error);
      toast.error('Erreur lors de la mise à jour de la description');
    }
  };

  const handleUpdateSessionReasons = async (newReasons: string[]) => {
    if (!selectedSession) return;

    console.log('Saving reasons to backend:', newReasons);
    try {
      const updated = await sessionService.update(selectedSession.id, { reasons: newReasons });
      console.log('Backend response:', updated);
      setSelectedSession({ ...selectedSession, reasons: newReasons });
      loadSessions(); // Refresh the list
      toast.success('Raisons sauvegardées');
    } catch (error) {
      console.error('Error updating session reasons:', error);
      toast.error('Erreur lors de la mise à jour des raisons');
    }
  };

  const handleAddReason = () => {
    const newReasons = [...reasons, ''];
    setReasons(newReasons);
    // Don't save to backend yet - wait for user to type something
  };

  const handleUpdateReason = (index: number, value: string) => {
    const newReasons = [...reasons];
    newReasons[index] = value;
    setReasons(newReasons);
  };

  const handleSaveReason = (index: number, value: string) => {
    const newReasons = [...reasons];
    newReasons[index] = value;
    setReasons(newReasons);
    // Filter out empty reasons before saving to backend
    const nonEmptyReasons = newReasons.filter(r => r.trim() !== '');
    handleUpdateSessionReasons(nonEmptyReasons);
  };

  const handleRemoveReason = (index: number) => {
    const newReasons = reasons.filter((_, i) => i !== index);
    setReasons(newReasons);
    // Filter out empty reasons before saving to backend
    const nonEmptyReasons = newReasons.filter(r => r.trim() !== '');
    handleUpdateSessionReasons(nonEmptyReasons);
  };

  const handleGeneratePreview = async () => {
    if (!selectedSession) return;

    try {
      setGeneratingPdf(true);

      // Revoke old blob URL if exists
      if (pdfUrl && pdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfUrl);
      }

      // Generate preview (this will save it to the database and return blob URL)
      const blobUrl = await sessionService.generatePreview(selectedSession.id);
      setPdfUrl(blobUrl);

      // Reload session to update it with the saved preview_pdf_url (for future loads)
      const updatedSession = await sessionService.getSession(selectedSession.id);
      setSelectedSession(updatedSession);

      toast.success('Preview généré avec succès');
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Erreur lors de la génération du preview');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleToggleItemSelection = (itemId: number) => {
    setSelectedItemIds(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  // Handle mouse down on item to start drag selection
  const handleItemMouseDown = (itemId: number, event: React.MouseEvent) => {
    event.preventDefault();
    setIsDraggingSelection(true);
    const isCurrentlySelected = selectedItemIds.includes(itemId);
    setDragStartAction(isCurrentlySelected ? 'deselect' : 'select');
    handleToggleItemSelection(itemId);
  };

  // Handle mouse enter on item during drag
  const handleItemMouseEnter = (itemId: number) => {
    if (isDraggingSelection && dragStartAction) {
      if (dragStartAction === 'select' && !selectedItemIds.includes(itemId)) {
        setSelectedItemIds(prev => [...prev, itemId]);
      } else if (dragStartAction === 'deselect' && selectedItemIds.includes(itemId)) {
        setSelectedItemIds(prev => prev.filter(id => id !== itemId));
      }
    }
  };

  // Handle mouse up to end drag selection
  const handleItemMouseUp = () => {
    setIsDraggingSelection(false);
    setDragStartAction(null);
  };

  // Global mouse up listener to end drag even if mouse leaves items
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDraggingSelection) {
        setIsDraggingSelection(false);
        setDragStartAction(null);
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => document.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isDraggingSelection]);

  // Handle click outside to deselect
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Don't deselect if clicking on session action buttons
      if (target.closest('[data-session-actions]')) {
        return;
      }

      // Check if click is inside the session items area
      const sessionItemsContainer = document.querySelector('[data-session-items-container]');
      if (sessionItemsContainer && !sessionItemsContainer.contains(target)) {
        // Click is outside the session items container, deselect all
        setSelectedItemIds([]);
      }
    };

    // Only add listener if there are selected items
    if (selectedItemIds.length > 0) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [selectedItemIds]);

  const handleGeneratePartialPreview = async () => {
    if (!selectedSession || selectedItemIds.length === 0) {
      toast.error('Veuillez sélectionner au moins un élément');
      return;
    }

    try {
      setGeneratingPartialPdf(true);

      // Revoke old blob URL if exists
      if (partialPdfUrl) {
        URL.revokeObjectURL(partialPdfUrl);
      }

      const blobUrl = await sessionService.generatePartialPreview(selectedSession.id, selectedItemIds);
      setPartialPdfUrl(blobUrl);
      setPartialPreviewOpen(true);
      toast.success('Preview partiel généré avec succès');
    } catch (error) {
      console.error('Error generating partial preview:', error);
      toast.error('Erreur lors de la génération du preview partiel');
    } finally {
      setGeneratingPartialPdf(false);
    }
  };

  const handleVisualizeFullSession = async () => {
    if (!selectedSession) return;
    try {
      setGeneratingFullPdf(true);
      if (fullPdfUrl) {
        URL.revokeObjectURL(fullPdfUrl);
        setFullPdfUrl(null);
      }
      const blobUrl = await sessionService.generatePreview(selectedSession.id);
      setFullPdfUrl(blobUrl);
      setFullPreviewOpen(true);
      const updatedSession = await sessionService.getSession(selectedSession.id);
      setSelectedSession(updatedSession);
    } catch (error) {
      console.error('Error generating full preview:', error);
      toast.error('Erreur lors de la génération du preview');
    } finally {
      setGeneratingFullPdf(false);
    }
  };

  const handleShuffleItems = async () => {
    if (!selectedSession || sessionItems.length < 2) return;
    try {
      setShuffleLoading(true);
      const shuffled = [...sessionItems];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      setSessionItems(shuffled);
      const itemsOrder = shuffled.map((item, idx) => ({ id: item.id, order: idx }));
      await sessionService.reorderItems(selectedSession.id, itemsOrder);
      toast.success('Ordre mélangé');
    } catch (error) {
      console.error('Error shuffling items:', error);
      toast.error('Erreur lors du mélange');
      loadSessions();
    } finally {
      setShuffleLoading(false);
    }
  };

  const handleCloseFullPreview = () => {
    setFullPreviewOpen(false);
    if (fullPdfUrl) {
      URL.revokeObjectURL(fullPdfUrl);
      setFullPdfUrl(null);
    }
  };

  const handleRefondre = async () => {
    if (!selectedSession) return;
    const hasExercises = sessionItems.some(item => item.item_type === 'exercise');
    if (!hasExercises) {
      toast.info('Aucun exercice à refondre dans cette séance');
      return;
    }
    try {
      setRefondreLoading(true);
      const result = await sessionService.refondre(selectedSession.id);
      setSelectedSession(result.session);
      setSessionItems(result.session.items || []);
      if (result.errors.length > 0) {
        toast.warning(`${result.regenerated} exercice(s) refondus, ${result.errors.length} erreur(s)`);
      } else {
        toast.success(`${result.regenerated} exercice(s) refondus avec succès`);
      }
    } catch (error) {
      console.error('Error refondre:', error);
      toast.error('Erreur lors de la refonte des exercices');
    } finally {
      setRefondreLoading(false);
    }
  };

  const handleOpenCompositeConfig = async (groupId: string) => {
    const groupItems = sessionItems.filter((i: any) => i.composite_config?.group_id === groupId);
    if (groupItems.length === 0) return;
    const config = groupItems[0].composite_config;

    // Load all pattern titles from the composite pattern
    try {
      const composite = await compositePatternService.get(config.composite_id);
      const allPatterns = composite.items?.map((item: any) => ({ id: item.exercise_pattern, title: item.pattern_title })) || [];
      setEditCompositeAllPatterns(allPatterns);
    } catch {
      setEditCompositeAllPatterns(config.pool.map((id: number) => ({ id, title: `Pattern #${id}` })));
    }

    setEditingCompositeGroupId(groupId);
    setEditCompositePool(new Set(config.pool));
    setEditCompositeCount(config.count);
    setEditCompositeConfigOpen(true);
  };

  const handleSaveCompositeConfig = async () => {
    if (!editingCompositeGroupId || !selectedSession) return;
    const groupItems = sessionItems.filter((i: any) => i.composite_config?.group_id === editingCompositeGroupId);
    if (groupItems.length === 0) return;
    const newConfig = {
      ...groupItems[0].composite_config,
      pool: Array.from(editCompositePool),
      count: editCompositeCount,
    };
    try {
      await api.post(`/sessions/${selectedSession.id}/update_item_config/`, {
        group_id: editingCompositeGroupId,
        composite_config: newConfig,
      });
      const updatedSession = await sessionService.getSession(selectedSession.id);
      setSelectedSession(updatedSession);
      setSessionItems(updatedSession.items || []);
      toast.success('Configuration composite mise à jour');
    } catch (error) {
      console.error('Error saving composite config:', error);
      toast.error('Erreur lors de la mise à jour');
    }
    setEditCompositeConfigOpen(false);
  };

  const handleGenerateSolutions = async () => {
    if (!selectedSession) return;
    try {
      setSolutionsLoading(true);
      if (solutionsPdfUrl) URL.revokeObjectURL(solutionsPdfUrl);
      const url = await sessionService.generateSolutionsPdf(selectedSession.id);
      setSolutionsPdfUrl(url);
      setSolutionsPreviewOpen(true);
    } catch (error) {
      console.error('Error generating solutions:', error);
      toast.error('Erreur lors de la génération des solutions');
    } finally {
      setSolutionsLoading(false);
    }
  };

  const handleSavePreference = async (key: string, value: any) => {
    const update: any = {};
    if (key === 'separator_after_course') { setSepAfterCourse(value); update.separator_after_course = value; }
    if (key === 'separator_after_exercise') { setSepAfterExercise(value); update.separator_after_exercise = value; }
    if (key === 'exercise_numbering') { setExerciseNumbering(value); update.exercise_numbering = value; }
    try {
      await sessionService.updatePreferences(update);
    } catch (e) {
      console.error('Error saving preference:', e);
    }
  };

  const handleClosePartialPreview = () => {
    setPartialPreviewOpen(false);
    if (partialPdfUrl) {
      URL.revokeObjectURL(partialPdfUrl);
      setPartialPdfUrl(null);
    }
  };

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const handleSelectSession = async (session: any) => {
    setSelectedSession(session);
    setName(session.name);
    setDescription(session.description || '');
    // Filter out any empty reasons when loading
    console.log('Session data:', session);
    console.log('Session reasons:', session.reasons);
    const loadedReasons = (session.reasons || []).filter((r: string) => r && r.trim() !== '');
    console.log('Loaded reasons after filter:', loadedReasons);
    setReasons(loadedReasons);
    setIsPublic(session.is_public);
    setSessionItems(session.items || []);

    // Load saved PDF preview or clear if changing sessions
    if (pdfUrl && pdfUrl.startsWith('blob:')) {
      URL.revokeObjectURL(pdfUrl);
    }

    // If there's a saved PDF, fetch it and create a blob URL
    if (session.preview_pdf_url) {
      try {
        const response = await fetch(session.preview_pdf_url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setPdfUrl(blobUrl);
      } catch (error) {
        console.error('Error loading saved PDF:', error);
        setPdfUrl(null);
      }
    } else {
      setPdfUrl(null);
    }

    // Clear selection when changing sessions
    setSelectedItemIds([]);
  };

  const handleDelete = async (sessionId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette séance ?')) {
      return;
    }

    try {
      await sessionService.delete(sessionId);
      toast.success('Séance supprimée');
      resetForm();
      loadSessions();
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDuplicate = async (sessionId: number) => {
    try {
      await sessionService.duplicate(sessionId);
      toast.success('Séance dupliquée');
      loadSessions();
    } catch (error) {
      console.error('Error duplicating session:', error);
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleRemoveItem = async (itemId: number) => {
    if (!selectedSession) return;

    try {
      await sessionService.removeItem(selectedSession.id, itemId);
      const updatedItems = sessionItems.filter(item => item.id !== itemId);
      setSessionItems(updatedItems);
      toast.success('Élément retiré');
    } catch (error) {
      console.error('Error removing item:', error);
      toast.error("Erreur lors du retrait de l'élément");
    }
  };

  const handleAddNewpage = async () => {
    if (!selectedSession) return;
    try {
      const order = sessionItems.length;
      const newItem = await sessionService.addItem(selectedSession.id, { item_type: 'newpage', order });
      setSessionItems([...sessionItems, newItem]);
    } catch (error) {
      console.error('Error adding newpage:', error);
      toast.error("Erreur lors de l'ajout du saut de page");
    }
  };

  const handleNew = () => {
    setSessionDialogOpen(true);
    setCreateType('session');
  };

  const handleCreateContainer = async () => {
    if (!newContainerTitle.trim()) {
      toast.error('Le nom du contenant est requis');
      return;
    }

    try {
      await sessionContainerService.createContainer({
        title: newContainerTitle,
        description: newContainerDescription,
        parent: currentContainer,
      });
      toast.success('Contenant créé');
      setSessionDialogOpen(false);
      setNewContainerTitle('');
      setNewContainerDescription('');
      loadContainers();
    } catch (error) {
      console.error('Error creating container:', error);
      toast.error('Erreur lors de la création du contenant');
    }
  };

  const handleCreateNewSession = async () => {
    if (!newSessionName.trim()) {
      toast.error('Le nom de la séance est requis');
      return;
    }

    try {
      const newSession = await sessionService.create({
        name: newSessionName,
        container: currentContainer,
        tags: [],
      });
      toast.success('Séance créée');
      setSessionDialogOpen(false);
      setNewSessionName('');
      handleSelectSession(newSession);
      loadSessions();
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Erreur lors de la création de la séance');
    }
  };

  const handleEditContainer = async (container: any) => {
    setEditingContainer(container);
    setEditContainerTitle(container.title);
    setEditContainerDescription(container.description || '');
    setEditContainerThumbnail(container.thumbnail || null);
    setEditContainerDialogOpen(true);
  };

  const handleUpdateContainer = async () => {
    if (!editContainerTitle.trim()) {
      toast.error('Le nom du contenant est requis');
      return;
    }

    try {
      await sessionContainerService.updateContainer(editingContainer.id, {
        title: editContainerTitle,
        description: editContainerDescription,
        thumbnail: editContainerThumbnail,
      } as any);
      toast.success('Contenant mis à jour');
      setEditContainerDialogOpen(false);
      setEditingContainer(null);
      setEditContainerTitle('');
      setEditContainerDescription('');
      setEditContainerThumbnail(null);
      loadContainers();
    } catch (error) {
      console.error('Error updating container:', error);
      toast.error('Erreur lors de la mise à jour du contenant');
    }
  };

  const handleEditSessionName = (session: any, event: React.MouseEvent) => {
    event.stopPropagation();
    setEditingSessionName(session.name);
    setEditSessionNameDialogOpen(true);
  };

  const handleUpdateSessionName = async () => {
    if (!editingSessionName.trim()) {
      toast.error('Le nom de la séance est requis');
      return;
    }

    if (!selectedSession) return;

    try {
      await sessionService.update(selectedSession.id, {
        name: editingSessionName,
      });
      toast.success('Nom de la séance mis à jour');
      setEditSessionNameDialogOpen(false);
      setSelectedSession({ ...selectedSession, name: editingSessionName });
      setName(editingSessionName);
      loadSessions();
    } catch (error) {
      console.error('Error updating session name:', error);
      toast.error('Erreur lors de la mise à jour du nom');
    }
  };

  const handleDeleteContainer = async (containerId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contenant ?')) {
      return;
    }

    try {
      await sessionContainerService.deleteContainer(containerId);
      toast.success('Contenant supprimé');
      loadContainers();
    } catch (error) {
      console.error('Error deleting container:', error);
      toast.error('Erreur lors de la suppression du contenant');
    }
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;

    // Extract the ID from draggableId (format: "container-123", "session-456", "course-123", or "exercise-456")
    const [itemType, itemIdStr] = draggableId.split('-');
    const itemId = parseInt(itemIdStr);

    // Handle reordering session items
    if (source.droppableId === 'session-items' && destination.droppableId === 'session-items') {
      if (!selectedSession) return;

      const items = Array.from(sessionItems);
      const [reorderedItem] = items.splice(source.index, 1);
      items.splice(destination.index, 0, reorderedItem);

      setSessionItems(items);

      try {
        // Update order on backend
        const itemsOrder = items.map((item, idx) => ({
          id: item.id,
          order: idx,
        }));
        await sessionService.reorderItems(selectedSession.id, itemsOrder);
        toast.success('Ordre mis à jour');
      } catch (error) {
        console.error('Error reordering items:', error);
        toast.error("Erreur lors de la mise à jour de l'ordre");
        // Reload to revert changes
        loadSessions();
      }
      return;
    }

    // Handle dropping session into session items - import all items from the dropped session
    if (destination.droppableId === 'session-items' && itemType === 'sessiontab') {
      if (!selectedSession) {
        toast.error('Veuillez sélectionner une séance');
        return;
      }

      try {
        // Find the session being dropped
        const droppedSession = sessionsForTab.find(s => s.id === itemId);
        if (!droppedSession) return;

        // Get full session details with items
        const fullSession = await sessionService.getSession(itemId);

        if (!fullSession.items || fullSession.items.length === 0) {
          toast.info('Cette séance ne contient aucun élément');
          return;
        }

        // Store IDs of added items to identify them later
        const addedItemIds: number[] = [];

        // Add all items from the dropped session
        for (const item of fullSession.items) {
          const itemData: any = {
            item_type: item.item_type,
          };

          if (item.item_type === 'course') {
            itemData.course_id = item.course;
          } else if (item.item_type === 'exercise') {
            itemData.exercise_id = item.exercise;
          } else if (item.item_type === 'video') {
            itemData.video_id = item.video;
          }

          const addedItem = await sessionService.addItem(selectedSession.id, itemData);
          addedItemIds.push(addedItem.id);
        }

        toast.success(`${addedItemIds.length} élément(s) importé(s) de la séance "${fullSession.name}"`);

        // Reload session to get all items
        const updatedSession = await sessionService.getSession(selectedSession.id);

        // Find the newly added items and move them to the correct position
        const items = updatedSession.items || [];
        const addedItems = items.filter((item: any) => addedItemIds.includes(item.id));
        const otherItems = items.filter((item: any) => !addedItemIds.includes(item.id));

        // Insert added items at the destination index
        otherItems.splice(destination.index, 0, ...addedItems);

        // Reorder all items
        const itemsOrder = otherItems.map((item: any, idx: number) => ({
          id: item.id,
          order: idx,
        }));
        await sessionService.reorderItems(selectedSession.id, itemsOrder);

        // Reload again to get final state
        const finalSession = await sessionService.getSession(selectedSession.id);
        setSelectedSession(finalSession);
        setSessionItems(finalSession.items || []);
      } catch (error) {
        console.error('Error importing session items:', error);
        toast.error("Erreur lors de l'importation des éléments");
      }
      return;
    }

    // Handle dropping teacher's course into session items
    if (destination.droppableId === 'session-items' && itemType === 'teachercourse') {
      if (!selectedSession) {
        toast.error('Veuillez sélectionner une séance');
        return;
      }

      try {
        const itemData: any = {
          item_type: 'course',
          course_id: itemId,
        };

        const addedItem = await sessionService.addItem(selectedSession.id, itemData);
        toast.success('Cours ajouté à la séance');

        // Reload session to get all items
        const updatedSession = await sessionService.getSession(selectedSession.id);

        // Find the newly added item and move it to the correct position
        const items = updatedSession.items || [];
        const newItemIndex = items.findIndex((item: any) => item.id === addedItem.id);
        if (newItemIndex !== -1 && newItemIndex !== destination.index) {
          const [movedItem] = items.splice(newItemIndex, 1);
          items.splice(destination.index, 0, movedItem);

          // Reorder all items
          const itemsOrder = items.map((item: any, idx: number) => ({
            id: item.id,
            order: idx,
          }));
          await sessionService.reorderItems(selectedSession.id, itemsOrder);
        }

        // Reload again to get final state
        const finalSession = await sessionService.getSession(selectedSession.id);
        setSelectedSession(finalSession);
        setSessionItems(finalSession.items || []);
      } catch (error) {
        console.error('Error adding teacher course:', error);
        toast.error("Erreur lors de l'ajout du cours");
      }
      return;
    }

    // Handle dropping teacher's exercise into session items
    if (destination.droppableId === 'session-items' && itemType === 'teacherexercise') {
      if (!selectedSession) {
        toast.error('Veuillez sélectionner une séance');
        return;
      }

      try {
        const itemData: any = {
          item_type: 'exercise',
          exercise_id: itemId,
        };

        const addedItem = await sessionService.addItem(selectedSession.id, itemData);
        toast.success('Exercice ajouté à la séance');

        // Reload session to get all items
        const updatedSession = await sessionService.getSession(selectedSession.id);

        // Find the newly added item and move it to the correct position
        const items = updatedSession.items || [];
        const newItemIndex = items.findIndex((item: any) => item.id === addedItem.id);
        if (newItemIndex !== -1 && newItemIndex !== destination.index) {
          const [movedItem] = items.splice(newItemIndex, 1);
          items.splice(destination.index, 0, movedItem);

          // Reorder all items
          const itemsOrder = items.map((item: any, idx: number) => ({
            id: item.id,
            order: idx,
          }));
          await sessionService.reorderItems(selectedSession.id, itemsOrder);
        }

        // Reload again to get final state
        const finalSession = await sessionService.getSession(selectedSession.id);
        setSelectedSession(finalSession);
        setSessionItems(finalSession.items || []);
      } catch (error) {
        console.error('Error adding teacher exercise:', error);
        toast.error("Erreur lors de l'ajout de l'exercice");
      }
      return;
    }

    // Handle dropping teacher's video into session items
    if (destination.droppableId === 'session-items' && itemType === 'teachervideo') {
      if (!selectedSession) {
        toast.error('Veuillez sélectionner une séance');
        return;
      }

      try {
        const itemData: any = {
          item_type: 'video',
          video_id: itemId,
        };

        const addedItem = await sessionService.addItem(selectedSession.id, itemData);
        toast.success('Vidéo ajoutée à la séance');

        // Reload session to get all items
        const updatedSession = await sessionService.getSession(selectedSession.id);

        // Find the newly added item and move it to the correct position
        const items = updatedSession.items || [];
        const newItemIndex = items.findIndex((item: any) => item.id === addedItem.id);
        if (newItemIndex !== -1 && newItemIndex !== destination.index) {
          const [movedItem] = items.splice(newItemIndex, 1);
          items.splice(destination.index, 0, movedItem);

          // Reorder all items
          const itemsOrder = items.map((item: any, idx: number) => ({
            id: item.id,
            order: idx,
          }));
          await sessionService.reorderItems(selectedSession.id, itemsOrder);
        }

        // Reload again to get final state
        const finalSession = await sessionService.getSession(selectedSession.id);
        setSelectedSession(finalSession);
        setSessionItems(finalSession.items || []);
      } catch (error) {
        console.error('Error adding teacher video:', error);
        toast.error("Erreur lors de l'ajout de la vidéo");
      }
      return;
    }

    // Handle dropping teacher's session into session items - import all items
    if (destination.droppableId === 'session-items' && itemType === 'teachersession') {
      if (!selectedSession) {
        toast.error('Veuillez sélectionner une séance');
        return;
      }

      try {
        const fullSession = await sessionService.getSession(itemId);

        if (!fullSession.items || fullSession.items.length === 0) {
          toast.info('Cette séance ne contient aucun élément');
          return;
        }

        // Store IDs of added items to identify them later
        const addedItemIds: number[] = [];

        // Add all items from the teacher's session
        for (const item of fullSession.items) {
          const itemData: any = {
            item_type: item.item_type,
          };

          if (item.item_type === 'course') {
            itemData.course_id = item.course;
          } else if (item.item_type === 'exercise') {
            itemData.exercise_id = item.exercise;
          } else if (item.item_type === 'video') {
            itemData.video_id = item.video;
          }

          const addedItem = await sessionService.addItem(selectedSession.id, itemData);
          addedItemIds.push(addedItem.id);
        }

        toast.success(`${addedItemIds.length} élément(s) importé(s) de la séance "${fullSession.name}"`);

        // Reload session to get all items
        const updatedSession = await sessionService.getSession(selectedSession.id);

        // Find the newly added items and move them to the correct position
        const items = updatedSession.items || [];
        const addedItems = items.filter((item: any) => addedItemIds.includes(item.id));
        const otherItems = items.filter((item: any) => !addedItemIds.includes(item.id));

        // Insert added items at the destination index
        otherItems.splice(destination.index, 0, ...addedItems);

        // Reorder all items
        const itemsOrder = otherItems.map((item: any, idx: number) => ({
          id: item.id,
          order: idx,
        }));
        await sessionService.reorderItems(selectedSession.id, itemsOrder);

        // Reload again to get final state
        const finalSession = await sessionService.getSession(selectedSession.id);
        setSelectedSession(finalSession);
        setSessionItems(finalSession.items || []);
      } catch (error) {
        console.error('Error importing teacher session items:', error);
        toast.error("Erreur lors de l'importation des éléments");
      }
      return;
    }

    // Handle dropping teacher's session item into session items
    if (destination.droppableId === 'session-items' && itemType === 'teachersessionitem') {
      if (!selectedSession) {
        toast.error('Veuillez sélectionner une séance');
        return;
      }

      try {
        // The draggableId format is "teachersessionitem-{item_type}-{id}"
        const parts = draggableId.split('-');
        const sessionItemType = parts[1]; // 'course', 'exercise', or 'video'
        const sessionItemId = parseInt(parts[2]);

        const itemData: any = {
          item_type: sessionItemType,
        };

        if (sessionItemType === 'course') {
          itemData.course_id = sessionItemId;
        } else if (sessionItemType === 'exercise') {
          itemData.exercise_id = sessionItemId;
        } else if (sessionItemType === 'video') {
          itemData.video_id = sessionItemId;
        }

        const addedItem = await sessionService.addItem(selectedSession.id, itemData);
        toast.success('Élément ajouté à la séance');

        // Reload session to get all items
        const updatedSession = await sessionService.getSession(selectedSession.id);

        // Find the newly added item and move it to the correct position
        const items = updatedSession.items || [];
        const newItemIndex = items.findIndex((item: any) => item.id === addedItem.id);
        if (newItemIndex !== -1 && newItemIndex !== destination.index) {
          const [movedItem] = items.splice(newItemIndex, 1);
          items.splice(destination.index, 0, movedItem);

          // Reorder all items
          const itemsOrder = items.map((item: any, idx: number) => ({
            id: item.id,
            order: idx,
          }));
          await sessionService.reorderItems(selectedSession.id, itemsOrder);
        }

        // Reload again to get final state
        const finalSession = await sessionService.getSession(selectedSession.id);
        setSelectedSession(finalSession);
        setSessionItems(finalSession.items || []);
      } catch (error) {
        console.error('Error adding teacher session item:', error);
        toast.error("Erreur lors de l'ajout de l'élément");
      }
      return;
    }

    // Handle dropping session tab's session item into session items
    if (destination.droppableId === 'session-items' && itemType === 'sessiontabitem') {
      if (!selectedSession) {
        toast.error('Veuillez sélectionner une séance');
        return;
      }

      try {
        // The draggableId format is "sessiontabitem-{item_type}-{id}"
        const parts = draggableId.split('-');
        const sessionItemType = parts[1]; // 'course', 'exercise', or 'video'
        const sessionItemId = parseInt(parts[2]);

        const itemData: any = {
          item_type: sessionItemType,
        };

        if (sessionItemType === 'course') {
          itemData.course_id = sessionItemId;
        } else if (sessionItemType === 'exercise') {
          itemData.exercise_id = sessionItemId;
        } else if (sessionItemType === 'video') {
          itemData.video_id = sessionItemId;
        }

        const addedItem = await sessionService.addItem(selectedSession.id, itemData);
        toast.success('Élément ajouté à la séance');

        // Reload session to get all items
        const updatedSession = await sessionService.getSession(selectedSession.id);

        // Find the newly added item and move it to the correct position
        const items = updatedSession.items || [];
        const newItemIndex = items.findIndex((item: any) => item.id === addedItem.id);
        if (newItemIndex !== -1 && newItemIndex !== destination.index) {
          const [movedItem] = items.splice(newItemIndex, 1);
          items.splice(destination.index, 0, movedItem);

          // Reorder all items
          const itemsOrder = items.map((item: any, idx: number) => ({
            id: item.id,
            order: idx,
          }));
          await sessionService.reorderItems(selectedSession.id, itemsOrder);
        }

        // Reload again to get final state
        const finalSession = await sessionService.getSession(selectedSession.id);
        setSelectedSession(finalSession);
        setSessionItems(finalSession.items || []);
      } catch (error) {
        console.error('Error adding session tab item:', error);
        toast.error("Erreur lors de l'ajout de l'élément");
      }
      return;
    }

    // Handle dropping composite pattern into session items
    if (destination.droppableId === 'session-items' && itemType === 'compositepattern') {
      if (!selectedSession) {
        toast.error('Veuillez sélectionner une séance');
        return;
      }

      const cp = compositePatterns.find(c => c.id === itemId);
      if (!cp) return;

      // Need to fetch full details (with items) if not already loaded
      try {
        const fullCp = await compositePatternService.get(cp.id);
        setPendingDropDestinationIndex(destination.index);
        setSelectedCompositePattern(fullCp);
        setCompositeModalOpen(true);
      } catch (error) {
        console.error('Error loading composite pattern:', error);
        toast.error('Erreur lors du chargement du pattern composé');
      }
      return;
    }

    // Handle dropping pattern into session items
    if (destination.droppableId === 'session-items' && (itemType === 'pattern' || itemType === 'teacherpattern')) {
      if (!selectedSession) {
        toast.error('Veuillez sélectionner une séance');
        return;
      }

      // Find the pattern
      let pattern;
      if (itemType === 'pattern') {
        pattern = patterns.find(p => p.id === itemId);
      } else {
        pattern = teacherPatterns.find(p => p.id === itemId);
      }

      if (!pattern) return;

      // Store the destination index and pattern, then open modal
      setPendingDropDestinationIndex(destination.index);
      setSelectedPatternForInstances(pattern);
      setPatternInstanceModalOpen(true);
      return;
    }

    // Handle dropping course, exercise, or video into session items
    if (destination.droppableId === 'session-items' && (itemType === 'course' || itemType === 'exercise' || itemType === 'video')) {
      if (!selectedSession) {
        toast.error('Veuillez sélectionner une séance');
        return;
      }

      try {
        // Prepare data for API call with the correct drop position
        const itemData: any = {
          item_type: itemType,
          order: destination.index,
        };

        if (itemType === 'course') {
          const course = courses.find(c => c.id === itemId);
          if (!course) return;
          itemData.course_id = course.id;
        } else if (itemType === 'exercise') {
          const exercise = exercises.find(e => e.id === itemId);
          if (!exercise) return;
          itemData.exercise_id = exercise.id;
        } else if (itemType === 'video') {
          const video = videos.find(v => v.id === itemId);
          if (!video) return;
          itemData.video_id = video.id;
        }

        // Add item via API at the specified order
        const addedItem = await sessionService.addItem(selectedSession.id, itemData);
        toast.success('Élément ajouté à la séance');

        // Insert locally at the correct position and reindex
        const currentItems = [...sessionItems];
        currentItems.splice(destination.index, 0, addedItem);
        const reindexed = currentItems.map((item: any, idx: number) => ({ ...item, order: idx }));

        // Update backend order for all items
        await sessionService.reorderItems(
          selectedSession.id,
          reindexed.map((item: any, idx: number) => ({ id: item.id, order: idx }))
        );

        // Reload final state
        const finalSession = await sessionService.getSession(selectedSession.id);
        setSelectedSession(finalSession);
        setSessionItems(finalSession.items || []);
      } catch (error) {
        console.error('Error adding item:', error);
        toast.error("Erreur lors de l'ajout de l'élément");
      }
      return;
    }

    // Check if we're moving between different droppables
    if (source.droppableId !== destination.droppableId) {
      // Moving item between containers or to/from root
      if (itemType === 'session') {
        // Determine the target container
        let targetContainer = null;
        if (destination.droppableId === 'parent-container') {
          // Moving to parent container
          targetContainer = parentContainer;
        } else if (destination.droppableId.startsWith('container-')) {
          targetContainer = parseInt(destination.droppableId.split('-')[1]);
        }

        try {
          await sessionService.update(itemId, { container: targetContainer });
          toast.success('Séance déplacée');
          loadSessions();
          loadContainers();
        } catch (error) {
          console.error('Error moving session:', error);
          toast.error('Erreur lors du déplacement');
        }
      } else if (itemType === 'container') {
        // Moving container to another container
        let targetContainer = null;
        if (destination.droppableId === 'parent-container') {
          // Moving to parent container
          targetContainer = parentContainer;
        } else if (destination.droppableId.startsWith('container-')) {
          targetContainer = parseInt(destination.droppableId.split('-')[1]);
        }

        try {
          await sessionContainerService.updateContainer(itemId, { parent: targetContainer });
          toast.success('Contenant déplacé');
          loadContainers();
        } catch (error) {
          console.error('Error moving container:', error);
          toast.error('Erreur lors du déplacement');
        }
      }
      return;
    }

    // Reordering within same droppable
    if (itemType === 'container') {
      const items = Array.from(containers);
      const [reorderedItem] = items.splice(source.index, 1);
      items.splice(destination.index, 0, reorderedItem);

      setContainers(items);

      try {
        const itemsOrder = items.map((item, idx) => ({
          id: item.id,
          order: idx,
        }));
        await sessionContainerService.reorderContainers(itemsOrder);
        toast.success('Ordre mis à jour');
      } catch (error) {
        console.error('Error reordering containers:', error);
        toast.error("Erreur lors de la mise à jour de l'ordre");
        loadContainers();
      }
    } else if (itemType === 'session') {
      const items = Array.from(sessions);
      const [reorderedItem] = items.splice(source.index, 1);
      items.splice(destination.index, 0, reorderedItem);

      setSessions(items);

      try {
        // TODO: Add reorder API endpoint for sessions
        // const itemsOrder = items.map((item, idx) => ({
        //   id: item.id,
        //   order: idx,
        // }));
        // await sessionService.reorder(itemsOrder);
        toast.success('Ordre mis à jour');
      } catch (error) {
        console.error('Error reordering sessions:', error);
        toast.error("Erreur lors de la mise à jour de l'ordre");
        loadSessions();
      }
    }
  };

  const resetForm = () => {
    setSelectedSession(null);
    setName('');
    setIsPublic(false);
    setSessionItems([]);
  };

  const handlePatternInstanceConfirm = async (count: number, containerId: number | null) => {
    if (!selectedSession || !selectedPatternForInstances || pendingDropDestinationIndex === null) {
      return;
    }

    try {
      // Step 1: Ensure the container exists or create it
      let targetContainerId = containerId;
      if (!targetContainerId) {
        // Check if default container exists
        const allContainers = await exerciseContainerService.getContainers(null);
        let defaultContainer = allContainers.find(
          (c: any) => c.title === 'Exercices générés à la volée pour peupler une séance'
        );

        if (!defaultContainer) {
          // Create the default container
          defaultContainer = await exerciseContainerService.createContainer({
            title: 'Exercices générés à la volée pour peupler une séance',
            parent: null,
          });
          toast.info('Container par défaut créé');
        }
        targetContainerId = defaultContainer.id;
      }

      // Step 2: Create exercises from the pattern
      const createdExercises = [];
      for (let i = 0; i < count; i++) {
        const createdExercise = await exerciseService.createExerciseFromPattern(selectedPatternForInstances.id);
        // Update the container for the created exercise
        await exerciseService.updateExercise(createdExercise.id, { container: targetContainerId });
        // Reload the exercise to get the updated data
        const updatedExercise = await exerciseService.getExercise(createdExercise.id);
        createdExercises.push(updatedExercise);
      }

      toast.success(`${count} exercice${count > 1 ? 's' : ''} créé${count > 1 ? 's' : ''}`);

      // Step 3: Add all created exercises to the session at the correct positions
      const addedItems = [];
      for (let i = 0; i < createdExercises.length; i++) {
        const itemData = {
          item_type: 'exercise' as const,
          exercise_id: createdExercises[i].id,
          order: pendingDropDestinationIndex + i,
        };
        const addedItem = await sessionService.addItem(selectedSession.id, itemData);
        addedItems.push(addedItem);
      }

      // Step 4: Insert locally at the correct position and reindex all
      const currentItems = [...sessionItems];
      currentItems.splice(pendingDropDestinationIndex, 0, ...addedItems);
      const reindexed = currentItems.map((item: any, idx: number) => ({ ...item, order: idx }));

      // Update backend order for all items
      await sessionService.reorderItems(
        selectedSession.id,
        reindexed.map((item: any, idx: number) => ({ id: item.id, order: idx }))
      );

      // Step 5: Reload final state
      const finalSession = await sessionService.getSession(selectedSession.id);
      setSelectedSession(finalSession);
      setSessionItems(finalSession.items || []);

      toast.success(`Exercices ajoutés à la séance à la position ${pendingDropDestinationIndex + 1}`);
    } catch (error) {
      console.error('Error creating exercises from pattern:', error);
      toast.error('Erreur lors de la création des exercices');
    } finally {
      // Reset state
      setPatternInstanceModalOpen(false);
      setSelectedPatternForInstances(null);
      setPendingDropDestinationIndex(null);
    }
  };

  const handleCompositePatternConfirm = async (
    selectedPatternIds: number[],
    countsPerPattern: Record<number, number>,
    containerId: number | null,
    compositeConfig?: { composite_id: number; pool: number[]; count: number; group_id: string }
  ) => {
    if (!selectedSession || pendingDropDestinationIndex === null) return;

    try {
      // Ensure container exists
      let targetContainerId = containerId;
      if (!targetContainerId) {
        const allContainers = await exerciseContainerService.getContainers(null);
        let defaultContainer = allContainers.find(
          (c: any) => c.title === 'Exercices générés à la volée pour peupler une séance'
        );
        if (!defaultContainer) {
          defaultContainer = await exerciseContainerService.createContainer({
            title: 'Exercices générés à la volée pour peupler une séance',
            parent: null,
          });
        }
        targetContainerId = defaultContainer.id;
      }

      // Create exercises for each selected pattern
      const addedItems = [];
      let insertIdx = pendingDropDestinationIndex;
      for (const patternId of selectedPatternIds) {
        const count = countsPerPattern[patternId] || 1;
        for (let i = 0; i < count; i++) {
          const createdExercise = await exerciseService.createExerciseFromPattern(patternId);
          await exerciseService.updateExercise(createdExercise.id, { container: targetContainerId });
          const itemData: any = {
            item_type: 'exercise' as const,
            exercise_id: createdExercise.id,
            order: insertIdx,
          };
          if (compositeConfig) {
            itemData.composite_config = compositeConfig;
          }
          const addedItem = await sessionService.addItem(selectedSession.id, itemData);
          addedItems.push(addedItem);
          insertIdx++;
        }
      }

      // Reindex
      const currentItems = [...sessionItems];
      currentItems.splice(pendingDropDestinationIndex, 0, ...addedItems);
      const reindexed = currentItems.map((item: any, idx: number) => ({ ...item, order: idx }));
      await sessionService.reorderItems(
        selectedSession.id,
        reindexed.map((item: any, idx: number) => ({ id: item.id, order: idx }))
      );

      const finalSession = await sessionService.getSession(selectedSession.id);
      setSelectedSession(finalSession);
      setSessionItems(finalSession.items || []);

      toast.success(`${addedItems.length} exercice${addedItems.length > 1 ? 's' : ''} ajouté${addedItems.length > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error creating exercises from composite pattern:', error);
      toast.error('Erreur lors de la création des exercices');
    } finally {
      setCompositeModalOpen(false);
      setSelectedCompositePattern(null);
      setPendingDropDestinationIndex(null);
    }
  };

  const handleSelectTeacher = (teacher: User) => {
    setSelectedTeacher(teacher);
    setTeacherSubTab(0); // Reset to first tab
    setSelectedTeacherSession(null); // Reset selected session
    loadTeacherContent(teacher.id);
  };

  const handleSelectTeacherSession = async (session: any) => {
    try {
      // Load full session details with items
      const fullSession = await sessionService.getSession(session.id);
      setSelectedTeacherSession(fullSession);
    } catch (error) {
      console.error('Error loading session details:', error);
      toast.error('Erreur lors du chargement de la séance');
    }
  };

  const handleSelectSessionForTab = async (session: any) => {
    try {
      // Load full session details with items
      const fullSession = await sessionService.getSession(session.id);
      setSelectedSessionForTab(fullSession);
    } catch (error) {
      console.error('Error loading session details:', error);
      toast.error('Erreur lors du chargement de la séance');
    }
  };

  // Filter items based on search query and current tab
  const filteredCourses = courses.filter(course =>
    course.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredPatterns = patterns.filter(pattern =>
    pattern.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    pattern.latex_template?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredExercises = exercises.filter(exercise =>
    exercise.pattern_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    exercise.rendered_latex?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredVideos = videos.filter(video =>
    video.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    video.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredSessionsForTab = sessionsForTab.filter(session =>
    session.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTeachers = teachers.filter(teacher =>
    teacher.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    teacher.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    teacher.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    teacher.last_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Grid container spacing={3}>
        {/* Left Panel - Sessions List */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: 'calc(100vh - 250px)', overflow: 'auto' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Séances</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleNew}
                size="small"
              >
                Nouveau
              </Button>
            </Box>
            <Box display="flex" alignItems="center" mb={1}>
              <Switch size="small" checked={showDescriptions} onChange={(e) => setShowDescriptions(e.target.checked)} />
              <Typography variant="caption" color="text.secondary">Descriptions</Typography>
            </Box>

            {/* Parent navigation */}
            {currentContainer !== null && (
              <Droppable droppableId="parent-container" type="SESSION">
                {(provided, snapshot) => (
                  <Card
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      mb: 1,
                      cursor: 'pointer',
                      backgroundColor: snapshot.isDraggingOver ? 'action.selected' : 'action.hover',
                      transition: 'background-color 0.2s',
                    }}
                    onClick={() => { setCurrentContainer(parentContainer); onNavChange?.(parentContainer, null, parentContainerTitle); }}
                  >
                    <CardContent sx={{ py: 1 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <UpIcon color="action" />
                        <Typography variant="body2" fontWeight="bold">..</Typography>
                      </Box>
                      {provided.placeholder}
                    </CardContent>
                  </Card>
                )}
              </Droppable>
            )}

            {/* Containers */}
            <Droppable droppableId="containers" type="CONTAINER">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {containers.map((container, index) => (
                      <Draggable key={container.id} draggableId={`container-${container.id}`} index={index}>
                        {(provided, snapshot) => (
                          <div ref={provided.innerRef} {...provided.draggableProps}>
                            <Card
                              sx={{
                                mb: 1,
                                cursor: 'pointer',
                                border: '2px dashed',
                                borderColor: 'primary.main',
                                backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                              }}
                            >
                              <CardContent sx={{ py: 1 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box {...provided.dragHandleProps}>
                                    <DragIcon sx={{ cursor: 'grab', color: 'action.active' }} />
                                  </Box>
                                  <Droppable droppableId={`container-${container.id}`} type="SESSION">
                                    {(dropProvided, dropSnapshot) => (
                                      <Box
                                        ref={dropProvided.innerRef}
                                        {...dropProvided.droppableProps}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          flex: 1,
                                          minHeight: '20px',
                                          backgroundColor: dropSnapshot.isDraggingOver ? 'action.selected' : 'transparent',
                                          borderRadius: 1,
                                          transition: 'background-color 0.2s',
                                        }}
                                        onClick={() => { setCurrentContainer(container.id); onNavChange?.(container.id, null, container.title); }}
                                      >
                                        {container.thumbnail_url ? (
                                          <Avatar src={container.thumbnail_url} variant="rounded" sx={{ width: 40, height: 40, cursor: 'pointer', mr: 1 }} />
                                        ) : (
                                          <FolderIcon color="primary" sx={{ cursor: 'pointer', mr: 1 }} />
                                        )}
                                        <Typography variant="body2" sx={{ cursor: 'pointer' }}>
                                          {container.title}
                                        </Typography>
                                        {dropProvided.placeholder}
                                      </Box>
                                    )}
                                  </Droppable>
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditContainer(container); }}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteContainer(container.id); }}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                                {showDescriptions && container.description && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 5 }}>
                                    {container.description}
                                  </Typography>
                                )}
                              </CardContent>
                            </Card>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>

              {/* Sessions */}
              {loading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : (
                <Droppable droppableId="sessions" type="SESSION">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {sessions.map((session, index) => (
                        <Draggable key={session.id} draggableId={`session-${session.id}`} index={index}>
                          {(provided, snapshot) => (
                            <Card
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              sx={{
                                mb: 1,
                                cursor: 'pointer',
                                backgroundColor: selectedSession?.id === session.id
                                  ? 'action.selected'
                                  : snapshot.isDragging
                                  ? 'action.hover'
                                  : session.is_public
                                  ? 'rgba(33, 150, 243, 0.08)'
                                  : 'inherit',
                              }}
                              onClick={() => handleSelectSession(session)}
                            >
                              <CardContent sx={{ py: 1 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box {...provided.dragHandleProps}>
                                    <DragIcon sx={{ cursor: 'grab', color: 'action.active', fontSize: 20 }} />
                                  </Box>
                                  <FileIcon sx={{ fontSize: 20, color: 'action.disabled' }} />
                                  <Box flex={1} minWidth={0}>
                                    <Typography variant="body2" fontWeight="bold">
                                      {session.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {session.items?.length || 0} éléments
                                    </Typography>
                                  </Box>
                                  <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-end" flexShrink={0}>
                                    {/* Première ligne : Public/Privé Switch */}
                                    <Box display="flex" gap={0.5} alignItems="center">
                                      <Switch
                                        size="small"
                                        checked={session.is_public}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleToggleSessionPublic(session.id, e.target.checked);
                                        }}
                                      />
                                    </Box>
                                    {/* Deuxième ligne : Edit, Duplicate et Delete */}
                                    <Box display="flex" gap={0.5} alignItems="center">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => handleEditSessionName(session, e)}
                                        sx={{ color: 'action.active' }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); handleDuplicate(session.id); }}
                                        sx={{ color: 'action.active' }}
                                      >
                                        <DuplicateIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); handleDelete(session.id); }}
                                        sx={{ color: 'error.main' }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              )}
          </Paper>
        </Grid>

        {/* Right Panel - Session Details */}
        <Grid item xs={12} md={8}>
          {selectedSession ? (
            <Grid container spacing={2}>
              {/* Left Sub-Panel - Session Items */}
              <Grid item xs={12} md={6}>
                <Paper sx={{
                  p: 2,
                  height: 'calc(100vh - 250px)',
                  overflow: 'auto',
                  backgroundColor: selectedSession?.is_public ? 'rgba(33, 150, 243, 0.08)' : 'background.paper',
                  borderRadius: 2
                }}>
                  <Box mb={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Éléments de la séance
                      </Typography>
                      <IconButton size="small" onClick={(e) => setPrefsOpen(prefsOpen ? false : true)}
                        title="Paramètres de mise en forme">
                        <TuneIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                    <Box display="flex" gap={0.75} flexWrap="wrap" data-session-actions>
                      <Button variant="outlined" size="small" onClick={handleAddNewpage}
                        sx={{ fontSize: '0.75rem', py: 0.4 }}>
                        Nouvelle page
                      </Button>
                      <Button variant="outlined" size="small"
                        startIcon={generatingPartialPdf ? <CircularProgress size={14} /> : <PdfIcon sx={{ fontSize: 16 }} />}
                        onClick={handleGeneratePartialPreview}
                        disabled={generatingPartialPdf || selectedItemIds.length === 0}
                        sx={{ fontSize: '0.75rem', py: 0.4 }}>
                        Visualiser{selectedItemIds.length > 0 ? ` (${selectedItemIds.length})` : ''}
                      </Button>
                      <Button variant="outlined" size="small"
                        startIcon={generatingFullPdf ? <CircularProgress size={14} /> : <PdfIcon sx={{ fontSize: 16 }} />}
                        onClick={handleVisualizeFullSession}
                        disabled={generatingFullPdf || sessionItems.length === 0}
                        sx={{ fontSize: '0.75rem', py: 0.4 }}>
                        Visualiser tout
                      </Button>
                      <Button variant="outlined" size="small"
                        startIcon={shuffleLoading ? <CircularProgress size={14} /> : <CasinoIcon sx={{ fontSize: 16 }} />}
                        onClick={handleShuffleItems}
                        disabled={shuffleLoading || sessionItems.length < 2}
                        sx={{ fontSize: '0.75rem', py: 0.4 }}>
                        Mélanger
                      </Button>
                      <Button variant="outlined" size="small"
                        startIcon={refondreLoading ? <CircularProgress size={14} /> : <RefondreIcon sx={{ fontSize: 16 }} />}
                        onClick={handleRefondre}
                        disabled={refondreLoading || !sessionItems.some((item: any) => item.item_type === 'exercise')}
                        sx={{ fontSize: '0.75rem', py: 0.4 }}>
                        Refondre
                      </Button>
                      <Button variant="outlined" size="small"
                        startIcon={solutionsLoading ? <CircularProgress size={14} /> : <SolutionsIcon sx={{ fontSize: 16 }} />}
                        onClick={handleGenerateSolutions}
                        disabled={solutionsLoading || !sessionItems.some((item: any) => item.item_type === 'exercise')}
                        sx={{ fontSize: '0.75rem', py: 0.4 }}>
                        Solutions
                      </Button>
                    </Box>
                  </Box>

                  {prefsOpen && (
                    <Box sx={{ mt: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
                      <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>Mise en forme</Typography>
                      <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                          <InputLabel sx={{ fontSize: '0.75rem' }}>Après cours</InputLabel>
                          <Select value={sepAfterCourse} label="Après cours"
                            onChange={(e) => handleSavePreference('separator_after_course', e.target.value)}
                            sx={{ fontSize: '0.75rem' }}>
                            <MenuItem value="none">Rien</MenuItem>
                            <MenuItem value="hrulefill">Ligne</MenuItem>
                            <MenuItem value="dotted">Pointillés</MenuItem>
                            <MenuItem value="spaces">Espace</MenuItem>
                            <MenuItem value="double_hrulefill">Double ligne</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                          <InputLabel sx={{ fontSize: '0.75rem' }}>Après exercice</InputLabel>
                          <Select value={sepAfterExercise} label="Après exercice"
                            onChange={(e) => handleSavePreference('separator_after_exercise', e.target.value)}
                            sx={{ fontSize: '0.75rem' }}>
                            <MenuItem value="none">Rien</MenuItem>
                            <MenuItem value="hrulefill">Ligne</MenuItem>
                            <MenuItem value="dotted">Pointillés</MenuItem>
                            <MenuItem value="spaces">Espace</MenuItem>
                            <MenuItem value="double_hrulefill">Double ligne</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControlLabel
                          control={<Switch size="small" checked={exerciseNumbering}
                            onChange={(e) => handleSavePreference('exercise_numbering', e.target.checked)} />}
                          label={<Typography variant="caption">Numéroter</Typography>}
                        />
                      </Box>
                    </Box>
                  )}

                  <Box data-session-items-container>
                  <Droppable droppableId="session-items" type="CONTENT_ITEM">
                    {(provided, snapshot) => (
                      <List
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          minHeight: '200px',
                          backgroundColor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                          borderRadius: 1,
                          transition: 'background-color 0.2s',
                        }}
                      >
                        {sessionItems.map((item, index) => (
                          <Draggable key={item.id} draggableId={`session-item-${item.id}`} index={index}>
                            {(provided, snapshot) => item.item_type === 'newpage' ? (
                              <Card
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                sx={{
                                  mb: 1,
                                  backgroundColor: snapshot.isDragging
                                    ? 'action.hover'
                                    : selectedItemIds.includes(item.id)
                                    ? 'primary.light'
                                    : 'transparent',
                                  border: selectedItemIds.includes(item.id) ? '2px dashed' : '1px dashed',
                                  borderColor: selectedItemIds.includes(item.id) ? 'primary.main' : 'divider',
                                  transition: 'all 0.2s',
                                }}
                              >
                                <Box
                                  sx={{
                                    py: 0.25, px: 1, display: 'flex', alignItems: 'center', gap: 1,
                                    userSelect: isDraggingSelection ? 'none' : 'auto',
                                    cursor: 'pointer',
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => {
                                    const target = e.target as HTMLElement;
                                    if (!target.closest('button')) {
                                      handleItemMouseDown(item.id, e);
                                    }
                                  }}
                                  onMouseEnter={() => handleItemMouseEnter(item.id)}
                                  onMouseUp={handleItemMouseUp}
                                >
                                  <Box
                                    {...provided.dragHandleProps}
                                    onMouseDown={(e) => e.stopPropagation()}
                                  >
                                    <DragIcon sx={{ cursor: 'grab', color: 'action.active', fontSize: 16 }} />
                                  </Box>
                                  <Box sx={{ flex: 1, borderTop: '2px dashed', borderColor: 'text.disabled' }} />
                                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                    Nouvelle page
                                  </Typography>
                                  <Box sx={{ flex: 1, borderTop: '2px dashed', borderColor: 'text.disabled' }} />
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveItem(item.id);
                                    }}
                                    sx={{ color: 'error.main', p: 0.25 }}
                                  >
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Box>
                              </Card>
                            ) : (
                              <Card
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                sx={{
                                  mb: 1,
                                  backgroundColor: snapshot.isDragging
                                    ? 'action.hover'
                                    : selectedItemIds.includes(item.id)
                                    ? 'primary.light'
                                    : 'inherit',
                                  border: selectedItemIds.includes(item.id) ? '2px solid' : item.composite_config ? '2px solid' : '1px solid',
                                  borderColor: selectedItemIds.includes(item.id) ? 'primary.main' : item.composite_config ? 'error.main' : 'divider',
                                  transition: 'all 0.2s',
                                }}
                              >
                                <CardContent
                                  sx={{
                                    py: 1,
                                    userSelect: isDraggingSelection ? 'none' : 'auto',
                                    cursor: 'pointer',
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => {
                                    // Don't trigger selection if clicking on interactive elements
                                    const target = e.target as HTMLElement;
                                    if (!target.closest('button') && !target.closest('img')) {
                                      handleItemMouseDown(item.id, e);
                                    }
                                  }}
                                  onMouseEnter={() => handleItemMouseEnter(item.id)}
                                  onMouseUp={handleItemMouseUp}
                                >
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Box
                                      {...provided.dragHandleProps}
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      <DragIcon sx={{ cursor: 'grab', color: 'action.active', fontSize: 20 }} />
                                    </Box>
                                    {item.item_type === 'video' ? (
                                      <>
                                        {item.video_thumbnail_url && (
                                          <Box
                                            component="img"
                                            src={item.video_thumbnail_url}
                                            alt={item.video_title || 'Video thumbnail'}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setThumbnailPreviewUrl(item.video_thumbnail_url);
                                            }}
                                            onError={(e: any) => {
                                              e.target.style.display = 'none';
                                            }}
                                            sx={{
                                              width: 60,
                                              height: 40,
                                              objectFit: 'cover',
                                              borderRadius: 1,
                                              border: '1px solid',
                                              borderColor: 'divider',
                                              cursor: 'pointer',
                                              '&:hover': {
                                                opacity: 0.8,
                                              },
                                            }}
                                          />
                                        )}
                                        <Typography variant="body2" sx={{ flex: 1 }}>
                                          🎥 {item.video_title}
                                        </Typography>
                                      </>
                                    ) : item.item_type === 'course' ? (
                                      <>
                                        {item.course_thumbnail_url && (
                                          <Box
                                            component="img"
                                            src={item.course_thumbnail_url}
                                            alt={item.course_title || 'Course thumbnail'}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setThumbnailPreviewUrl(item.course_thumbnail_url);
                                            }}
                                            onError={(e: any) => {
                                              e.target.style.display = 'none';
                                            }}
                                            sx={{
                                              width: 60,
                                              height: 40,
                                              objectFit: 'cover',
                                              borderRadius: 1,
                                              border: '1px solid',
                                              borderColor: 'divider',
                                              cursor: 'pointer',
                                              '&:hover': {
                                                opacity: 0.8,
                                              },
                                            }}
                                          />
                                        )}
                                        <Typography variant="body2" sx={{ flex: 1 }}>
                                          📖 {item.course_title}
                                        </Typography>
                                      </>
                                    ) : (
                                      <>
                                        {item.exercise_pattern_thumbnail_url && (
                                          <Box
                                            component="img"
                                            src={item.exercise_pattern_thumbnail_url}
                                            alt={item.exercise_pattern || 'Exercise pattern thumbnail'}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setThumbnailPreviewUrl(item.exercise_pattern_thumbnail_url);
                                            }}
                                            onError={(e: any) => {
                                              e.target.style.display = 'none';
                                            }}
                                            sx={{
                                              width: 60,
                                              height: 40,
                                              objectFit: 'cover',
                                              borderRadius: 1,
                                              border: '1px solid',
                                              borderColor: 'divider',
                                              cursor: 'pointer',
                                              '&:hover': {
                                                opacity: 0.8,
                                              },
                                            }}
                                          />
                                        )}
                                        <Typography variant="body2" sx={{ flex: 1 }}>
                                          ✏️ {item.exercise_pattern}
                                        </Typography>
                                      </>
                                    )}
                                    {item.composite_config && (
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenCompositeConfig(item.composite_config.group_id);
                                        }}
                                        sx={{ color: 'error.main' }}
                                      >
                                        <CasinoIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveItem(item.id);
                                      }}
                                      sx={{ color: 'error.main' }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </CardContent>
                              </Card>
                            )}
                          </Draggable>
                        ))}
                        {sessionItems.length === 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                            Glissez-déposez des cours ou exercices ici
                          </Typography>
                        )}
                        {provided.placeholder}
                      </List>
                    )}
                  </Droppable>
                  </Box>
                </Paper>
              </Grid>

              {/* Right Sub-Panel - Content Search and List with Tabs */}
              <Grid item xs={12} md={6}>
                <Paper sx={{
                  p: 2,
                  height: 'calc(100vh - 250px)',
                  overflow: 'auto',
                  backgroundColor: selectedSession?.is_public ? 'rgba(33, 150, 243, 0.08)' : 'background.paper',
                  borderRadius: 2
                }}>
                  <Tabs
                    value={currentTab}
                    onChange={(e, newValue) => {
                      setCurrentTab(newValue);
                      setSearchQuery(''); // Reset search when changing tabs
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      minHeight: '36px',
                      '& .MuiTab-root': {
                        fontSize: '0.75rem',
                        minHeight: '36px',
                        padding: '6px 6px',
                        minWidth: 'auto',
                      }
                    }}
                  >
                    <Tab label="Cours" />
                    <Tab label="Patterns" />
                    <Tab label="Exercices" />
                    <Tab label="Séances" />
                    <Tab label="Cours vidéo" />
                    <Tab label="Enseignants" />
                  </Tabs>

                  <TextField
                    fullWidth
                    label="Rechercher"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    margin="normal"
                    size="small"
                    placeholder="Tapez pour rechercher..."
                  />

                  <Box sx={{ mt: 2 }}>
                    {/* Tab 0: Cours */}
                    {currentTab === 0 && (
                      <>
                        {/* Parent navigation */}
                        {currentCourseContainer !== null && (
                          <Card
                            sx={{
                              mb: 1,
                              cursor: 'pointer',
                              backgroundColor: 'action.hover',
                            }}
                            onClick={() => setCurrentCourseContainer(parentCourseContainer)}
                          >
                            <CardContent sx={{ py: 1 }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <UpIcon color="action" />
                                <Typography variant="body2" fontWeight="bold">..</Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        )}

                        {/* Course Containers */}
                        {courseContainers.map((container) => (
                          <Card
                            key={container.id}
                            sx={{
                              mb: 1,
                              cursor: 'pointer',
                              border: '2px dashed',
                              borderColor: 'primary.main',
                            }}
                            onClick={() => setCurrentCourseContainer(container.id)}
                          >
                            <CardContent sx={{ py: 1 }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <FolderIcon color="primary" />
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  {container.title}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        ))}

                        {/* Courses */}
                        <Droppable droppableId="courses-list" type="CONTENT_ITEM">
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                              {filteredCourses.length === 0 && courseContainers.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                  {searchQuery ? 'Aucun cours trouvé' : 'Aucun cours disponible'}
                                </Typography>
                              ) : (
                                filteredCourses.map((course, index) => (
                                  <Draggable key={`course-${course.id}`} draggableId={`course-${course.id}`} index={index}>
                                    {(provided, snapshot) => (
                                      <Card
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        sx={{
                                          mb: 1,
                                          cursor: 'grab',
                                          backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                                          '&:active': { cursor: 'grabbing' },
                                        }}
                                      >
                                        <CardContent sx={{ py: 1 }}>
                                          <Box display="flex" alignItems="center" gap={1}>
                                            <DragIcon sx={{ fontSize: 20, color: 'action.active' }} />
                                            {course.thumbnail_url ? (
                                              <Box
                                                component="img"
                                                src={course.thumbnail_url}
                                                alt={course.title}
                                                onError={(e: any) => {
                                                  e.target.style.display = 'none';
                                                }}
                                                sx={{
                                                  width: 40,
                                                  height: 30,
                                                  objectFit: 'cover',
                                                  borderRadius: 1,
                                                  border: '1px solid',
                                                  borderColor: 'divider',
                                                }}
                                              />
                                            ) : (
                                              <FileIcon sx={{ fontSize: 20 }} />
                                            )}
                                            <Typography variant="body2" sx={{ flex: 1 }}>
                                              {course.title}
                                            </Typography>
                                          </Box>
                                        </CardContent>
                                      </Card>
                                    )}
                                  </Draggable>
                                ))
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </>
                    )}

                    {/* Tab 1: Patterns */}
                    {currentTab === 1 && (
                      <>
                        {/* Parent navigation */}
                        {currentPatternContainer !== null && (
                          <Card
                            sx={{
                              mb: 1,
                              cursor: 'pointer',
                              backgroundColor: 'action.hover',
                            }}
                            onClick={() => setCurrentPatternContainer(parentPatternContainer)}
                          >
                            <CardContent sx={{ py: 1 }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <UpIcon color="action" />
                                <Typography variant="body2" fontWeight="bold">..</Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        )}

                        {/* Pattern Containers */}
                        {patternContainers.map((container) => (
                          <Card
                            key={container.id}
                            sx={{
                              mb: 1,
                              cursor: 'pointer',
                              border: '2px dashed',
                              borderColor: 'primary.main',
                            }}
                            onClick={() => setCurrentPatternContainer(container.id)}
                          >
                            <CardContent sx={{ py: 1 }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                {container.thumbnail_url ? (
                                  <Avatar src={container.thumbnail_url} variant="rounded" sx={{ width: 32, height: 32 }} />
                                ) : (
                                  <FolderIcon color="primary" />
                                )}
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  {container.title}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        ))}

                        {/* Composite Patterns */}
                        {compositePatterns.length > 0 && (
                          <Droppable droppableId="composite-patterns-list" type="CONTENT_ITEM">
                            {(provided) => (
                              <div ref={provided.innerRef} {...provided.droppableProps}>
                                {compositePatterns.map((cp, index) => (
                                  <Draggable key={`compositepattern-${cp.id}`} draggableId={`compositepattern-${cp.id}`} index={index}>
                                    {(provided, snapshot) => (
                                      <Card
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        sx={{
                                          mb: 1,
                                          cursor: 'grab',
                                          border: '2px solid',
                                          borderColor: 'secondary.main',
                                          backgroundColor: snapshot.isDragging ? 'rgba(156, 39, 176, 0.12)' : 'rgba(156, 39, 176, 0.04)',
                                          '&:active': { cursor: 'grabbing' },
                                        }}
                                      >
                                        <CardContent sx={{ py: 1 }}>
                                          <Box display="flex" alignItems="center" gap={1}>
                                            <DragIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
                                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
                                              {cp.title}
                                            </Typography>
                                            <Typography variant="caption" color="secondary">
                                              {cp.child_count}p
                                            </Typography>
                                          </Box>
                                        </CardContent>
                                      </Card>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        )}

                        {/* Patterns */}
                        <Droppable droppableId="patterns-list" type="CONTENT_ITEM">
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                              {filteredPatterns.length === 0 && patternContainers.length === 0 && compositePatterns.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                  {searchQuery ? 'Aucun pattern trouvé' : 'Aucun pattern disponible'}
                                </Typography>
                              ) : (
                                filteredPatterns.map((pattern, index) => (
                                  <Draggable key={`pattern-${pattern.id}`} draggableId={`pattern-${pattern.id}`} index={index}>
                                    {(provided, snapshot) => (
                                      <Card
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        sx={{
                                          mb: 1,
                                          cursor: 'grab',
                                          backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                                          '&:active': { cursor: 'grabbing' },
                                        }}
                                      >
                                        <CardContent sx={{ py: 1 }}>
                                          <Box display="flex" alignItems="center" gap={1}>
                                            <DragIcon sx={{ fontSize: 20, color: 'action.active' }} />
                                            {pattern.thumbnail_url ? (
                                              <Box
                                                component="img"
                                                src={pattern.thumbnail_url}
                                                alt={pattern.title || 'Pattern'}
                                                onError={(e: any) => {
                                                  e.target.style.display = 'none';
                                                }}
                                                sx={{
                                                  width: 40,
                                                  height: 30,
                                                  objectFit: 'cover',
                                                  borderRadius: 1,
                                                  border: '1px solid',
                                                  borderColor: 'divider',
                                                }}
                                              />
                                            ) : (
                                              <FileIcon sx={{ fontSize: 20 }} />
                                            )}
                                            <Typography variant="body2" sx={{ flex: 1 }}>
                                              {pattern.title || `Pattern ${pattern.id}`}
                                            </Typography>
                                          </Box>
                                        </CardContent>
                                      </Card>
                                    )}
                                  </Draggable>
                                ))
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </>
                    )}

                    {/* Tab 2: Exercices */}
                    {currentTab === 2 && (
                      <>
                        {/* Parent navigation */}
                        {currentExerciseContainer !== null && (
                          <Card
                            sx={{
                              mb: 1,
                              cursor: 'pointer',
                              backgroundColor: 'action.hover',
                            }}
                            onClick={() => setCurrentExerciseContainer(parentExerciseContainer)}
                          >
                            <CardContent sx={{ py: 1 }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <UpIcon color="action" />
                                <Typography variant="body2" fontWeight="bold">..</Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        )}

                        {/* Exercise Containers */}
                        {exerciseContainers.map((container) => (
                          <Card
                            key={container.id}
                            sx={{
                              mb: 1,
                              cursor: 'pointer',
                              border: '2px dashed',
                              borderColor: 'primary.main',
                            }}
                            onClick={() => setCurrentExerciseContainer(container.id)}
                          >
                            <CardContent sx={{ py: 1 }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <FolderIcon color="primary" />
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  {container.title}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        ))}

                        {/* Exercises */}
                        <Droppable droppableId="exercises-list" type="CONTENT_ITEM">
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                              {filteredExercises.length === 0 && exerciseContainers.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                  {searchQuery ? 'Aucun exercice trouvé' : 'Aucun exercice disponible'}
                                </Typography>
                              ) : (
                                filteredExercises.map((exercise, index) => (
                                  <Draggable key={`exercise-${exercise.id}`} draggableId={`exercise-${exercise.id}`} index={index}>
                                    {(provided, snapshot) => (
                                      <Card
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        sx={{
                                          mb: 1,
                                          cursor: 'grab',
                                          backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                                          '&:active': { cursor: 'grabbing' },
                                        }}
                                      >
                                        <CardContent sx={{ py: 1 }}>
                                          <Box display="flex" alignItems="center" gap={1}>
                                            <DragIcon sx={{ fontSize: 20, color: 'action.active' }} />
                                            {exercise.pattern_thumbnail_url ? (
                                              <Box
                                                component="img"
                                                src={exercise.pattern_thumbnail_url}
                                                alt={exercise.pattern_title || 'Exercise pattern'}
                                                onError={(e: any) => {
                                                  e.target.style.display = 'none';
                                                }}
                                                sx={{
                                                  width: 40,
                                                  height: 30,
                                                  objectFit: 'cover',
                                                  borderRadius: 1,
                                                  border: '1px solid',
                                                  borderColor: 'divider',
                                                }}
                                              />
                                            ) : (
                                              <FileIcon sx={{ fontSize: 20 }} />
                                            )}
                                            <Typography variant="body2" sx={{ flex: 1 }}>
                                              {exercise.pattern_title || `Exercice ${exercise.id}`}
                                            </Typography>
                                          </Box>
                                        </CardContent>
                                      </Card>
                                    )}
                                  </Draggable>
                                ))
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </>
                    )}

                    {/* Tab 3: Séances */}
                    {currentTab === 3 && (
                      <>
                        {/* Parent navigation */}
                        {currentSessionContainerForTab !== null && (
                          <Card
                            sx={{
                              mb: 1,
                              cursor: 'pointer',
                              backgroundColor: 'action.hover',
                            }}
                            onClick={() => setCurrentSessionContainerForTab(parentSessionContainerForTab)}
                          >
                            <CardContent sx={{ py: 1 }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <UpIcon color="action" />
                                <Typography variant="body2" fontWeight="bold">..</Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        )}

                        {/* Session Containers */}
                        {sessionContainersForTab.map((container) => (
                          <Card
                            key={container.id}
                            sx={{
                              mb: 1,
                              cursor: 'pointer',
                              border: '2px dashed',
                              borderColor: 'primary.main',
                            }}
                            onClick={() => setCurrentSessionContainerForTab(container.id)}
                          >
                            <CardContent sx={{ py: 1 }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <FolderIcon color="primary" />
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  {container.title}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        ))}

                        {/* Sessions */}
                        {!selectedSessionForTab ? (
                          // List of sessions
                          <Droppable droppableId="sessions-tab-list" type="CONTENT_ITEM">
                            {(provided) => (
                              <div ref={provided.innerRef} {...provided.droppableProps}>
                                {filteredSessionsForTab.length === 0 && sessionContainersForTab.length === 0 ? (
                                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                    {searchQuery ? 'Aucune séance trouvée' : 'Aucune séance disponible'}
                                  </Typography>
                                ) : (
                                  filteredSessionsForTab.map((session, index) => (
                                    <Draggable key={`sessiontab-${session.id}`} draggableId={`sessiontab-${session.id}`} index={index}>
                                      {(provided, snapshot) => (
                                        <Card
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          sx={{
                                            mb: 1,
                                            backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                                          }}
                                        >
                                          <CardContent sx={{ py: 1 }}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                              <Box {...provided.dragHandleProps}>
                                                <DragIcon sx={{ cursor: 'grab', color: 'action.active', fontSize: 20 }} />
                                              </Box>
                                              <SessionIcon
                                                sx={{ fontSize: 20, cursor: 'pointer' }}
                                                onClick={() => handleSelectSessionForTab(session)}
                                              />
                                              <Typography
                                                variant="body2"
                                                sx={{ flex: 1, cursor: 'pointer' }}
                                                onClick={() => handleSelectSessionForTab(session)}
                                              >
                                                {session.name}
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary">
                                                {session.items?.length || 0} éléments
                                              </Typography>
                                            </Box>
                                          </CardContent>
                                        </Card>
                                      )}
                                    </Draggable>
                                  ))
                                )}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        ) : (
                          // Session details
                          <>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {selectedSessionForTab.name}
                              </Typography>
                              <Button
                                size="small"
                                onClick={() => setSelectedSessionForTab(null)}
                              >
                                Retour
                              </Button>
                            </Box>

                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                              Éléments de la séance
                            </Typography>

                            <Droppable droppableId="sessiontab-session-items-list" type="CONTENT_ITEM">
                              {(provided) => (
                                <div ref={provided.innerRef} {...provided.droppableProps}>
                                  {selectedSessionForTab.items?.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                      Cette séance ne contient aucun élément
                                    </Typography>
                                  ) : (
                                    selectedSessionForTab.items?.map((item: any, index: number) => (
                                      <Draggable
                                        key={`sessiontabitem-${item.item_type}-${item.course || item.exercise || item.video}`}
                                        draggableId={`sessiontabitem-${item.item_type}-${item.course || item.exercise || item.video}`}
                                        index={index}
                                      >
                                        {(provided, snapshot) => (
                                          <Card
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            sx={{
                                              mb: 1,
                                              cursor: 'grab',
                                              backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                                              '&:active': { cursor: 'grabbing' },
                                            }}
                                          >
                                            <CardContent sx={{ py: 1 }}>
                                              <Box display="flex" alignItems="center" gap={1}>
                                                <DragIcon sx={{ fontSize: 20, color: 'action.active' }} />
                                                {item.item_type === 'video' ? (
                                                  <>
                                                    {item.video_thumbnail_url && (
                                                      <Box
                                                        component="img"
                                                        src={item.video_thumbnail_url}
                                                        alt={item.video_title || 'Video thumbnail'}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setThumbnailPreviewUrl(item.video_thumbnail_url);
                                                        }}
                                                        onError={(e: any) => {
                                                          e.target.style.display = 'none';
                                                        }}
                                                        sx={{
                                                          width: 60,
                                                          height: 40,
                                                          objectFit: 'cover',
                                                          borderRadius: 1,
                                                          border: '1px solid',
                                                          borderColor: 'divider',
                                                          cursor: 'pointer',
                                                          '&:hover': {
                                                            opacity: 0.8,
                                                          },
                                                        }}
                                                      />
                                                    )}
                                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                                      🎥 {item.video_title}
                                                    </Typography>
                                                  </>
                                                ) : item.item_type === 'course' ? (
                                                  <>
                                                    {item.course_thumbnail_url && (
                                                      <Box
                                                        component="img"
                                                        src={item.course_thumbnail_url}
                                                        alt={item.course_title || 'Course thumbnail'}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setThumbnailPreviewUrl(item.course_thumbnail_url);
                                                        }}
                                                        onError={(e: any) => {
                                                          e.target.style.display = 'none';
                                                        }}
                                                        sx={{
                                                          width: 60,
                                                          height: 40,
                                                          objectFit: 'cover',
                                                          borderRadius: 1,
                                                          border: '1px solid',
                                                          borderColor: 'divider',
                                                          cursor: 'pointer',
                                                          '&:hover': {
                                                            opacity: 0.8,
                                                          },
                                                        }}
                                                      />
                                                    )}
                                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                                      📖 {item.course_title}
                                                    </Typography>
                                                  </>
                                                ) : (
                                                  <>
                                                    {item.exercise_pattern_thumbnail_url && (
                                                      <Box
                                                        component="img"
                                                        src={item.exercise_pattern_thumbnail_url}
                                                        alt={item.exercise_pattern || 'Exercise pattern thumbnail'}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setThumbnailPreviewUrl(item.exercise_pattern_thumbnail_url);
                                                        }}
                                                        onError={(e: any) => {
                                                          e.target.style.display = 'none';
                                                        }}
                                                        sx={{
                                                          width: 60,
                                                          height: 40,
                                                          objectFit: 'cover',
                                                          borderRadius: 1,
                                                          border: '1px solid',
                                                          borderColor: 'divider',
                                                          cursor: 'pointer',
                                                          '&:hover': {
                                                            opacity: 0.8,
                                                          },
                                                        }}
                                                      />
                                                    )}
                                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                                      ✏️ {item.exercise_pattern}
                                                    </Typography>
                                                  </>
                                                )}
                                              </Box>
                                            </CardContent>
                                          </Card>
                                        )}
                                      </Draggable>
                                    ))
                                  )}
                                  {provided.placeholder}
                                </div>
                              )}
                            </Droppable>
                          </>
                        )}
                      </>
                    )}

                    {/* Tab 4: Cours vidéo */}
                    {currentTab === 4 && (
                      <>
                        {/* Parent navigation */}
                        {currentVideoContainer !== null && (
                          <Card
                            sx={{
                              mb: 1,
                              cursor: 'pointer',
                              backgroundColor: 'action.hover',
                            }}
                            onClick={() => setCurrentVideoContainer(parentVideoContainer)}
                          >
                            <CardContent sx={{ py: 1 }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <UpIcon color="action" />
                                <Typography variant="body2" fontWeight="bold">..</Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        )}

                        {/* Video Containers */}
                        {videoContainers.map((container) => (
                          <Card
                            key={container.id}
                            sx={{
                              mb: 1,
                              cursor: 'pointer',
                              border: '2px dashed',
                              borderColor: 'primary.main',
                            }}
                            onClick={() => setCurrentVideoContainer(container.id)}
                          >
                            <CardContent sx={{ py: 1 }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <FolderIcon color="primary" />
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  {container.title}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        ))}

                        {/* Videos */}
                        <Droppable droppableId="videos-list" type="CONTENT_ITEM">
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                              {filteredVideos.length === 0 && videoContainers.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                  {searchQuery ? 'Aucune vidéo trouvée' : 'Aucune vidéo disponible'}
                                </Typography>
                              ) : (
                                filteredVideos.map((video, index) => (
                                  <Draggable key={`video-${video.id}`} draggableId={`video-${video.id}`} index={index}>
                                    {(provided, snapshot) => (
                                      <Card
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        sx={{
                                          mb: 1,
                                          cursor: 'grab',
                                          backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                                          '&:active': { cursor: 'grabbing' },
                                        }}
                                      >
                                        <CardContent sx={{ py: 1 }}>
                                          <Box display="flex" alignItems="center" gap={1}>
                                            <DragIcon sx={{ fontSize: 20, color: 'action.active' }} />
                                            {video.thumbnail_url ? (
                                              <Box
                                                component="img"
                                                src={video.thumbnail_url}
                                                alt={video.title}
                                                onError={(e: any) => {
                                                  e.target.style.display = 'none';
                                                }}
                                                sx={{
                                                  width: 40,
                                                  height: 30,
                                                  objectFit: 'cover',
                                                  borderRadius: 1,
                                                  border: '1px solid',
                                                  borderColor: 'divider',
                                                }}
                                              />
                                            ) : (
                                              <FileIcon sx={{ fontSize: 20 }} />
                                            )}
                                            <Typography variant="body2" sx={{ flex: 1 }}>
                                              {video.title}
                                            </Typography>
                                          </Box>
                                        </CardContent>
                                      </Card>
                                    )}
                                  </Draggable>
                                ))
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </>
                    )}

                    {/* Tab 5: Enseignants */}
                    {currentTab === 5 && (
                      <>
                        {!selectedTeacher ? (
                          // List of teachers
                          <>
                            {filteredTeachers.length === 0 ? (
                              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                {searchQuery ? 'Aucun enseignant trouvé' : 'Aucun enseignant disponible'}
                              </Typography>
                            ) : (
                              <List>
                                {filteredTeachers.map((teacher) => (
                                  <Card
                                    key={teacher.id}
                                    sx={{
                                      mb: 1,
                                      cursor: 'pointer',
                                      '&:hover': { backgroundColor: 'action.hover' }
                                    }}
                                    onClick={() => handleSelectTeacher(teacher)}
                                  >
                                    <CardContent sx={{ py: 1 }}>
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="body2" sx={{ flex: 1, fontWeight: 'bold' }}>
                                          {teacher.first_name} {teacher.last_name}
                                        </Typography>
                                      </Box>
                                      <Typography variant="caption" color="text.secondary">
                                        {teacher.email}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                        @{teacher.username}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                ))}
                              </List>
                            )}
                          </>
                        ) : (
                          // Teacher details with subtabs
                          <>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                              <Typography variant="h6">
                                {selectedTeacher.first_name} {selectedTeacher.last_name}
                              </Typography>
                              <Button
                                size="small"
                                onClick={() => setSelectedTeacher(null)}
                              >
                                Retour
                              </Button>
                            </Box>

                            <Tabs
                              value={teacherSubTab}
                              onChange={(e, newValue) => setTeacherSubTab(newValue)}
                              variant="scrollable"
                              scrollButtons="auto"
                              sx={{
                                minHeight: '36px',
                                '& .MuiTab-root': {
                                  fontSize: '0.75rem',
                                  minHeight: '36px',
                                  padding: '6px 6px',
                                  minWidth: 'auto',
                                }
                              }}
                            >
                              <Tab label="Cours" />
                              <Tab label="Patterns" />
                              <Tab label="Exercices" />
                              <Tab label="Séances" />
                              <Tab label="Cours vidéo" />
                            </Tabs>

                            <Box sx={{ mt: 2 }}>
                              {/* Sub-tab 0: Cours */}
                              {teacherSubTab === 0 && (
                                <Droppable droppableId="teacher-courses-list" type="CONTENT_ITEM">
                                  {(provided) => (
                                    <div ref={provided.innerRef} {...provided.droppableProps}>
                                      {teacherCourses.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                          Aucun cours
                                        </Typography>
                                      ) : (
                                        teacherCourses.map((course, index) => (
                                          <Draggable key={`teachercourse-${course.id}`} draggableId={`teachercourse-${course.id}`} index={index}>
                                            {(provided, snapshot) => (
                                              <Card
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                sx={{
                                                  mb: 1,
                                                  cursor: 'grab',
                                                  backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                                                  '&:active': { cursor: 'grabbing' },
                                                }}
                                              >
                                                <CardContent sx={{ py: 1 }}>
                                                  <Box display="flex" alignItems="center" gap={1}>
                                                    <DragIcon sx={{ fontSize: 20, color: 'action.active' }} />
                                                    {course.thumbnail_url ? (
                                                      <Box
                                                        component="img"
                                                        src={course.thumbnail_url}
                                                        alt={course.title}
                                                        onError={(e: any) => {
                                                          e.target.style.display = 'none';
                                                        }}
                                                        sx={{
                                                          width: 40,
                                                          height: 30,
                                                          objectFit: 'cover',
                                                          borderRadius: 1,
                                                          border: '1px solid',
                                                          borderColor: 'divider',
                                                        }}
                                                      />
                                                    ) : (
                                                      <FileIcon sx={{ fontSize: 20 }} />
                                                    )}
                                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                                      {course.title}
                                                    </Typography>
                                                  </Box>
                                                </CardContent>
                                              </Card>
                                            )}
                                          </Draggable>
                                        ))
                                      )}
                                      {provided.placeholder}
                                    </div>
                                  )}
                                </Droppable>
                              )}

                              {/* Sub-tab 1: Patterns */}
                              {teacherSubTab === 1 && (
                                <Droppable droppableId="teacher-patterns-list" type="CONTENT_ITEM">
                                  {(provided) => (
                                    <div ref={provided.innerRef} {...provided.droppableProps}>
                                      {teacherPatterns.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                          Aucun pattern disponible
                                        </Typography>
                                      ) : (
                                        teacherPatterns.map((pattern, index) => (
                                          <Draggable key={`teacher-pattern-${pattern.id}`} draggableId={`teacher-pattern-${pattern.id}`} index={index}>
                                            {(provided, snapshot) => (
                                              <Card
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                sx={{
                                                  mb: 1,
                                                  cursor: 'grab',
                                                  backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                                                  '&:active': { cursor: 'grabbing' },
                                                }}
                                              >
                                                <CardContent sx={{ py: 1 }}>
                                                  <Box display="flex" alignItems="center" gap={1}>
                                                    <DragIcon sx={{ fontSize: 20, color: 'action.active' }} />
                                                    {pattern.thumbnail_url ? (
                                                      <Box
                                                        component="img"
                                                        src={pattern.thumbnail_url}
                                                        alt={pattern.title || 'Pattern'}
                                                        onError={(e: any) => {
                                                          e.target.style.display = 'none';
                                                        }}
                                                        sx={{
                                                          width: 40,
                                                          height: 30,
                                                          objectFit: 'cover',
                                                          borderRadius: 1,
                                                          border: '1px solid',
                                                          borderColor: 'divider',
                                                        }}
                                                      />
                                                    ) : (
                                                      <FileIcon sx={{ fontSize: 20 }} />
                                                    )}
                                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                                      {pattern.title || `Pattern ${pattern.id}`}
                                                    </Typography>
                                                  </Box>
                                                </CardContent>
                                              </Card>
                                            )}
                                          </Draggable>
                                        ))
                                      )}
                                      {provided.placeholder}
                                    </div>
                                  )}
                                </Droppable>
                              )}

                              {/* Sub-tab 2: Exercices */}
                              {teacherSubTab === 2 && (
                                <Droppable droppableId="teacher-exercises-list" type="CONTENT_ITEM">
                                  {(provided) => (
                                    <div ref={provided.innerRef} {...provided.droppableProps}>
                                      {teacherExercises.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                          Aucun exercice
                                        </Typography>
                                      ) : (
                                        teacherExercises.map((exercise, index) => (
                                          <Draggable key={`teacherexercise-${exercise.id}`} draggableId={`teacherexercise-${exercise.id}`} index={index}>
                                            {(provided, snapshot) => (
                                              <Card
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                sx={{
                                                  mb: 1,
                                                  cursor: 'grab',
                                                  backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                                                  '&:active': { cursor: 'grabbing' },
                                                }}
                                              >
                                                <CardContent sx={{ py: 1 }}>
                                                  <Box display="flex" alignItems="center" gap={1}>
                                                    <DragIcon sx={{ fontSize: 20, color: 'action.active' }} />
                                                    {exercise.pattern_thumbnail_url ? (
                                                      <Box
                                                        component="img"
                                                        src={exercise.pattern_thumbnail_url}
                                                        alt={exercise.pattern_title || 'Exercise pattern'}
                                                        onError={(e: any) => {
                                                          e.target.style.display = 'none';
                                                        }}
                                                        sx={{
                                                          width: 40,
                                                          height: 30,
                                                          objectFit: 'cover',
                                                          borderRadius: 1,
                                                          border: '1px solid',
                                                          borderColor: 'divider',
                                                        }}
                                                      />
                                                    ) : (
                                                      <FileIcon sx={{ fontSize: 20 }} />
                                                    )}
                                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                                      {exercise.pattern_title || `Exercice ${exercise.id}`}
                                                    </Typography>
                                                  </Box>
                                                </CardContent>
                                              </Card>
                                            )}
                                          </Draggable>
                                        ))
                                      )}
                                      {provided.placeholder}
                                    </div>
                                  )}
                                </Droppable>
                              )}

                              {/* Sub-tab 3: Séances */}
                              {teacherSubTab === 3 && (
                                <>
                                  {!selectedTeacherSession ? (
                                    // List of sessions
                                    <Droppable droppableId="teacher-sessions-list" type="CONTENT_ITEM">
                                      {(provided) => (
                                        <div ref={provided.innerRef} {...provided.droppableProps}>
                                          {teacherSessions.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                              Aucune séance
                                            </Typography>
                                          ) : (
                                            teacherSessions.map((session, index) => (
                                              <Draggable key={`teachersession-${session.id}`} draggableId={`teachersession-${session.id}`} index={index}>
                                                {(provided, snapshot) => (
                                                  <Card
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    sx={{
                                                      mb: 1,
                                                      backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                                                    }}
                                                  >
                                                    <CardContent sx={{ py: 1 }}>
                                                      <Box display="flex" alignItems="center" gap={1}>
                                                        <Box {...provided.dragHandleProps}>
                                                          <DragIcon sx={{ cursor: 'grab', color: 'action.active', fontSize: 20 }} />
                                                        </Box>
                                                        <SessionIcon
                                                          sx={{ fontSize: 20, cursor: 'pointer' }}
                                                          onClick={() => handleSelectTeacherSession(session)}
                                                        />
                                                        <Typography
                                                          variant="body2"
                                                          sx={{ flex: 1, cursor: 'pointer' }}
                                                          onClick={() => handleSelectTeacherSession(session)}
                                                        >
                                                          {session.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                          {session.items?.length || 0} éléments
                                                        </Typography>
                                                      </Box>
                                                    </CardContent>
                                                  </Card>
                                                )}
                                              </Draggable>
                                            ))
                                          )}
                                          {provided.placeholder}
                                        </div>
                                      )}
                                    </Droppable>
                                  ) : (
                                    // Session details
                                    <>
                                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                          {selectedTeacherSession.name}
                                        </Typography>
                                        <Button
                                          size="small"
                                          onClick={() => setSelectedTeacherSession(null)}
                                        >
                                          Retour
                                        </Button>
                                      </Box>

                                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                        Éléments de la séance
                                      </Typography>

                                      <Droppable droppableId="teacher-session-items-list" type="CONTENT_ITEM">
                                        {(provided) => (
                                          <div ref={provided.innerRef} {...provided.droppableProps}>
                                            {selectedTeacherSession.items?.length === 0 ? (
                                              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                                Cette séance ne contient aucun élément
                                              </Typography>
                                            ) : (
                                              selectedTeacherSession.items?.map((item: any, index: number) => (
                                                <Draggable
                                                  key={`teachersessionitem-${item.item_type}-${item.course || item.exercise || item.video}`}
                                                  draggableId={`teachersessionitem-${item.item_type}-${item.course || item.exercise || item.video}`}
                                                  index={index}
                                                >
                                                  {(provided, snapshot) => (
                                                    <Card
                                                      ref={provided.innerRef}
                                                      {...provided.draggableProps}
                                                      {...provided.dragHandleProps}
                                                      sx={{
                                                        mb: 1,
                                                        cursor: 'grab',
                                                        backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                                                        '&:active': { cursor: 'grabbing' },
                                                      }}
                                                    >
                                                      <CardContent sx={{ py: 1 }}>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                          <DragIcon sx={{ fontSize: 20, color: 'action.active' }} />
                                                          {item.item_type === 'video' ? (
                                                            <>
                                                              {item.video_thumbnail_url && (
                                                                <Box
                                                                  component="img"
                                                                  src={item.video_thumbnail_url}
                                                                  alt={item.video_title || 'Video thumbnail'}
                                                                  onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setThumbnailPreviewUrl(item.video_thumbnail_url);
                                                                  }}
                                                                  onError={(e: any) => {
                                                                    e.target.style.display = 'none';
                                                                  }}
                                                                  sx={{
                                                                    width: 60,
                                                                    height: 40,
                                                                    objectFit: 'cover',
                                                                    borderRadius: 1,
                                                                    border: '1px solid',
                                                                    borderColor: 'divider',
                                                                    cursor: 'pointer',
                                                                    '&:hover': {
                                                                      opacity: 0.8,
                                                                    },
                                                                  }}
                                                                />
                                                              )}
                                                              <Typography variant="body2" sx={{ flex: 1 }}>
                                                                🎥 {item.video_title}
                                                              </Typography>
                                                            </>
                                                          ) : item.item_type === 'course' ? (
                                                            <>
                                                              {item.course_thumbnail_url && (
                                                                <Box
                                                                  component="img"
                                                                  src={item.course_thumbnail_url}
                                                                  alt={item.course_title || 'Course thumbnail'}
                                                                  onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setThumbnailPreviewUrl(item.course_thumbnail_url);
                                                                  }}
                                                                  onError={(e: any) => {
                                                                    e.target.style.display = 'none';
                                                                  }}
                                                                  sx={{
                                                                    width: 60,
                                                                    height: 40,
                                                                    objectFit: 'cover',
                                                                    borderRadius: 1,
                                                                    border: '1px solid',
                                                                    borderColor: 'divider',
                                                                    cursor: 'pointer',
                                                                    '&:hover': {
                                                                      opacity: 0.8,
                                                                    },
                                                                  }}
                                                                />
                                                              )}
                                                              <Typography variant="body2" sx={{ flex: 1 }}>
                                                                📖 {item.course_title}
                                                              </Typography>
                                                            </>
                                                          ) : (
                                                            <>
                                                              {item.exercise_pattern_thumbnail_url && (
                                                                <Box
                                                                  component="img"
                                                                  src={item.exercise_pattern_thumbnail_url}
                                                                  alt={item.exercise_pattern || 'Exercise pattern thumbnail'}
                                                                  onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setThumbnailPreviewUrl(item.exercise_pattern_thumbnail_url);
                                                                  }}
                                                                  onError={(e: any) => {
                                                                    e.target.style.display = 'none';
                                                                  }}
                                                                  sx={{
                                                                    width: 60,
                                                                    height: 40,
                                                                    objectFit: 'cover',
                                                                    borderRadius: 1,
                                                                    border: '1px solid',
                                                                    borderColor: 'divider',
                                                                    cursor: 'pointer',
                                                                    '&:hover': {
                                                                      opacity: 0.8,
                                                                    },
                                                                  }}
                                                                />
                                                              )}
                                                              <Typography variant="body2" sx={{ flex: 1 }}>
                                                                ✏️ {item.exercise_pattern}
                                                              </Typography>
                                                            </>
                                                          )}
                                                        </Box>
                                                      </CardContent>
                                                    </Card>
                                                  )}
                                                </Draggable>
                                              ))
                                            )}
                                            {provided.placeholder}
                                          </div>
                                        )}
                                      </Droppable>
                                    </>
                                  )}
                                </>
                              )}

                              {/* Sub-tab 4: Cours vidéo */}
                              {teacherSubTab === 4 && (
                                <Droppable droppableId="teacher-videos-list" type="CONTENT_ITEM">
                                  {(provided) => (
                                    <div ref={provided.innerRef} {...provided.droppableProps}>
                                      {teacherVideos.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                          Aucune vidéo
                                        </Typography>
                                      ) : (
                                        teacherVideos.map((video, index) => (
                                          <Draggable key={`teachervideo-${video.id}`} draggableId={`teachervideo-${video.id}`} index={index}>
                                            {(provided, snapshot) => (
                                              <Card
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                sx={{
                                                  mb: 1,
                                                  cursor: 'grab',
                                                  backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                                                  '&:active': { cursor: 'grabbing' },
                                                }}
                                              >
                                                <CardContent sx={{ py: 1 }}>
                                                  <Box display="flex" alignItems="center" gap={1}>
                                                    <DragIcon sx={{ fontSize: 20, color: 'action.active' }} />
                                                    {video.thumbnail_url ? (
                                                      <Box
                                                        component="img"
                                                        src={video.thumbnail_url}
                                                        alt={video.title}
                                                        onError={(e: any) => {
                                                          e.target.style.display = 'none';
                                                        }}
                                                        sx={{
                                                          width: 40,
                                                          height: 30,
                                                          objectFit: 'cover',
                                                          borderRadius: 1,
                                                          border: '1px solid',
                                                          borderColor: 'divider',
                                                        }}
                                                      />
                                                    ) : (
                                                      <FileIcon sx={{ fontSize: 20 }} />
                                                    )}
                                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                                      {video.title}
                                                    </Typography>
                                                  </Box>
                                                </CardContent>
                                              </Card>
                                            )}
                                          </Draggable>
                                        ))
                                      )}
                                      {provided.placeholder}
                                    </div>
                                  )}
                                </Droppable>
                              )}
                            </Box>
                          </>
                        )}
                      </>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Full-Width PDF Preview and Description Section */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Preview et Description
                  </Typography>

                  <TextField
                    fullWidth
                    label="Description"
                    placeholder="L'intérêt de la séance (servira à la communication avec les parents)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onBlur={(e) => handleUpdateSessionDescription(e.target.value)}
                    margin="normal"
                    size="small"
                    multiline
                    rows={3}
                  />

                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Raisons
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddReason}
                        variant="outlined"
                      >
                        Ajouter une raison
                      </Button>
                    </Box>

                    {reasons.length === 0 ? (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 3,
                          textAlign: 'center',
                          bgcolor: 'grey.50',
                        }}
                      >
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          Aucune raison ajoutée. Pourquoi donner cette séance à un élève ?
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          (ex: renforcement, découverte d'un nouveau concept, préparation examen...)
                        </Typography>
                      </Paper>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {reasons.map((reason, index) => (
                          <Paper
                            key={index}
                            variant="outlined"
                            sx={{
                              p: 2,
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                            }}
                          >
                            <TextField
                              fullWidth
                              multiline
                              rows={2}
                              value={reason}
                              onChange={(e) => handleUpdateReason(index, e.target.value)}
                              onBlur={(e) => handleSaveReason(index, e.target.value)}
                              placeholder="Saisissez une raison..."
                              variant="outlined"
                              size="small"
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveReason(index)}
                              sx={{
                                color: 'error.main',
                                '&:hover': { bgcolor: 'error.lighter' },
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Paper>
                        ))}
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button
                      variant="contained"
                      startIcon={generatingPdf ? <CircularProgress size={20} /> : <PdfIcon />}
                      onClick={handleGeneratePreview}
                      disabled={generatingPdf}
                    >
                      {generatingPdf ? 'Génération...' : 'Générer preview'}
                    </Button>
                  </Box>

                  {pdfUrl && (
                    <Box sx={{ mt: 2 }}>
                      <iframe
                        src={pdfUrl}
                        width="100%"
                        height="600px"
                        style={{ border: 'none' }}
                        title={`Preview - ${selectedSession.name}`}
                      />
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography variant="body1" color="text.secondary">
                Sélectionnez une séance pour la modifier ou créez-en une nouvelle
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
      </DragDropContext>

      {/* Creation Dialog */}
      <Dialog open={sessionDialogOpen} onClose={() => setSessionDialogOpen(false)}>
        <DialogTitle>Nouveau</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={createType}
              exclusive
              onChange={(e, newValue) => newValue && setCreateType(newValue)}
              fullWidth
            >
              <ToggleButton value="container">
                <FolderIcon sx={{ mr: 1 }} />
                Contenant
              </ToggleButton>
              <ToggleButton value="session">
                <SessionIcon sx={{ mr: 1 }} />
                Nouvelle séance
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {createType === 'container' && (
            <>
              <TextField
                autoFocus
                margin="dense"
                label="Nom du contenant"
                fullWidth
                variant="outlined"
                value={newContainerTitle}
                onChange={(e) => setNewContainerTitle(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateContainer();
                  }
                }}
              />
              <TextField
                margin="dense"
                label="Description (optionnel)"
                fullWidth
                variant="outlined"
                multiline
                rows={2}
                value={newContainerDescription}
                onChange={(e) => setNewContainerDescription(e.target.value)}
              />
            </>
          )}
          {createType === 'session' && (
            <TextField
              autoFocus
              margin="dense"
              label="Nom de la séance"
              fullWidth
              variant="outlined"
              value={newSessionName}
              onChange={(e) => setNewSessionName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newSessionName.trim()) {
                  handleCreateNewSession();
                }
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSessionDialogOpen(false);
            setNewContainerTitle('');
            setNewContainerDescription('');
            setNewSessionName('');
          }}>Annuler</Button>
          {createType === 'container' && (
            <Button variant="contained" onClick={handleCreateContainer}>Créer</Button>
          )}
          {createType === 'session' && (
            <Button variant="contained" onClick={handleCreateNewSession}>Créer</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Container Dialog */}
      <Dialog open={editContainerDialogOpen} onClose={() => setEditContainerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier le contenant</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom du contenant"
            fullWidth
            variant="outlined"
            value={editContainerTitle}
            onChange={(e) => setEditContainerTitle(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && editContainerTitle.trim()) {
                handleUpdateContainer();
              }
            }}
          />
          <TextField
            margin="dense"
            label="Description (optionnel)"
            fullWidth
            variant="outlined"
            multiline
            rows={2}
            value={editContainerDescription}
            onChange={(e) => setEditContainerDescription(e.target.value)}
          />
          <ThumbnailPicker
            value={editContainerThumbnail}
            onChange={setEditContainerThumbnail}
            currentThumbnailUrl={editingContainer?.thumbnail_url}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditContainerDialogOpen(false);
            setEditingContainer(null);
            setEditContainerTitle('');
            setEditContainerDescription('');
            setEditContainerThumbnail(null);
          }}>Annuler</Button>
          <Button variant="contained" onClick={handleUpdateContainer}>Modifier</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Session Name Dialog */}
      <Dialog open={editSessionNameDialogOpen} onClose={() => setEditSessionNameDialogOpen(false)}>
        <DialogTitle>Modifier le nom de la séance</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom de la séance"
            fullWidth
            variant="outlined"
            value={editingSessionName}
            onChange={(e) => setEditingSessionName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && editingSessionName.trim()) {
                handleUpdateSessionName();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditSessionNameDialogOpen(false);
            setEditingSessionName('');
          }}>Annuler</Button>
          <Button variant="contained" onClick={handleUpdateSessionName}>Modifier</Button>
        </DialogActions>
      </Dialog>

      {/* Thumbnail Preview Dialog */}
      <Dialog
        open={!!thumbnailPreviewUrl}
        onClose={() => setThumbnailPreviewUrl(null)}
        maxWidth="md"
        fullWidth
        onClick={() => setThumbnailPreviewUrl(null)}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            cursor: 'pointer',
          }
        }}
      >
        <DialogContent sx={{ p: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {thumbnailPreviewUrl && (
            <Box
              component="img"
              src={thumbnailPreviewUrl}
              alt="Thumbnail preview"
              sx={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Partial Preview Modal */}
      <Dialog
        open={partialPreviewOpen}
        onClose={handleClosePartialPreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Preview partiel de la séance ({selectedItemIds.length} élément{selectedItemIds.length > 1 ? 's' : ''})
        </DialogTitle>
        <DialogContent>
          {partialPdfUrl && (
            <Box sx={{ width: '100%', height: '70vh' }}>
              <iframe
                src={partialPdfUrl}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                title="Partial Session Preview"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePartialPreview}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full Session Preview Modal */}
      <Dialog
        open={fullPreviewOpen}
        onClose={handleCloseFullPreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Preview de toute la séance{selectedSession ? ` — ${selectedSession.name}` : ''}
        </DialogTitle>
        <DialogContent>
          {fullPdfUrl && (
            <Box sx={{ width: '100%', height: '70vh' }}>
              <iframe
                src={fullPdfUrl}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                title="Full Session Preview"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFullPreview}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Solutions Preview Modal */}
      <Dialog open={solutionsPreviewOpen} onClose={() => { setSolutionsPreviewOpen(false); if (solutionsPdfUrl) { URL.revokeObjectURL(solutionsPdfUrl); setSolutionsPdfUrl(null); } }} maxWidth="lg" fullWidth>
        <DialogTitle>Solutions de la séance</DialogTitle>
        <DialogContent>
          {solutionsPdfUrl && (
            <Box sx={{ width: '100%', height: '70vh' }}>
              <iframe src={solutionsPdfUrl} width="100%" height="100%" style={{ border: 'none' }} title="Solutions" />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSolutionsPreviewOpen(false); if (solutionsPdfUrl) { URL.revokeObjectURL(solutionsPdfUrl); setSolutionsPdfUrl(null); } }}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Pattern Instance Modal */}
      <PatternInstanceModal
        open={patternInstanceModalOpen}
        onClose={() => {
          setPatternInstanceModalOpen(false);
          setSelectedPatternForInstances(null);
          setPendingDropDestinationIndex(null);
        }}
        onConfirm={handlePatternInstanceConfirm}
        patternTitle={selectedPatternForInstances?.title || ''}
      />

      <CompositePatternInstanceModal
        open={compositeModalOpen}
        onClose={() => {
          setCompositeModalOpen(false);
          setSelectedCompositePattern(null);
          setPendingDropDestinationIndex(null);
        }}
        onConfirm={handleCompositePatternConfirm}
        compositeId={selectedCompositePattern?.id || 0}
        compositeTitle={selectedCompositePattern?.title || ''}
        items={selectedCompositePattern?.items || []}
      />

      {/* Composite Config Editor Dialog */}
      <Dialog open={editCompositeConfigOpen} onClose={() => setEditCompositeConfigOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configuration du tirage aléatoire</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Lors de la refonte, les exercices de ce groupe seront re-tirés au hasard parmi les patterns cochés ci-dessous.
          </Typography>
          <Grid container spacing={1}>
            {editCompositeAllPatterns.map((p) => {
              const included = editCompositePool.has(p.id);
              return (
                <Grid item xs={6} key={p.id}>
                  <Box
                    onClick={() => {
                      setEditCompositePool(prev => {
                        const next = new Set(prev);
                        if (next.has(p.id)) next.delete(p.id);
                        else next.add(p.id);
                        return next;
                      });
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      py: 0.75,
                      px: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: included ? 'primary.main' : 'divider',
                      backgroundColor: included ? 'rgba(33, 150, 243, 0.08)' : 'transparent',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: included ? 'rgba(33, 150, 243, 0.15)' : 'action.hover' },
                    }}
                  >
                    <Checkbox checked={included} size="small" sx={{ p: 0, mr: 1 }} />
                    <Typography variant="body1">{p.title}</Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
          {editCompositePool.size > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {editCompositePool.size} pattern{editCompositePool.size > 1 ? 's' : ''} sélectionné{editCompositePool.size > 1 ? 's' : ''}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCompositeConfigOpen(false)}>Annuler</Button>
          <Button
            onClick={handleSaveCompositeConfig}
            variant="contained"
            disabled={editCompositePool.size === 0}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Sessions;
